import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, EventEmitter } from '@angular/core';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import * as html2pdf from 'html2pdf.js';
import { environment } from 'src/environments/environment';
import { MessageService } from 'primeng/api';


export interface PDFOptions {
  orientation?: 'portrait' | 'landscape';
  unit?: 'mm' | 'pt' | 'px' | 'in';
  format?: string | number[];
  margins?: { top: number; right: number; bottom: number; left: number };
  quality?: number;
  scale?: number;
  filename?: string;
  contentType?: 'profile' | 'comparison' | 'general';
}

export interface PDFProgress {
  currentPage: number;
  totalPages: number;
  stage: string;
  percentage: number;
}

@Injectable({
  providedIn: 'root'
})

export class PdfService {
  showLoader: EventEmitter<boolean> = new EventEmitter();
  pdfProgress: EventEmitter<PDFProgress> = new EventEmitter();
  private isGenerating = false;
  private isCancelled = false;
  private footerHeight = 20; // Footer height in mm

  constructor(private http: HttpClient, private messageService: MessageService) { }

  /**
   * Generate footer text with current date and time in EST
   */
  private generateFooterText(pageNumber: number, totalPages: number): string {
    try {
      const now = new Date();
      const estTime = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));

      const dateStr = estTime.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      const timeStr = estTime.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short'
      });

      return `For Financial Professional Use Only. This information is current as of ${dateStr} at ${timeStr} EST and is subject to change without notice.\n` +
        `The values and information provided are solely for comparative purposes and ARE NOT a replacement for the information and illustrations provided by the annuity provider.\n` +
        `This information is not intended as, and does not constitute, investment advice.\n` +
        `Copyright© ${estTime.getFullYear()} Beacon Annuity Solutions, LLC All Rights Reserved.\n` +
        `Page ${pageNumber} of ${totalPages}`;
    } catch (error) {
      console.warn('Error generating footer text, using fallback:', error);
      return `For Financial Professional Use Only. This information is current as of ${new Date().toLocaleDateString()} and is subject to change without notice.\n` +
        `The values and information provided are solely for comparative purposes and ARE NOT a replacement for the information and illustrations provided by the annuity provider.\n` +
        `This information is not intended as, and does not constitute, investment advice.\n` +
        `Copyright© ${new Date().getFullYear()} Beacon Annuity Solutions, LLC All Rights Reserved.\n` +
        `Page ${pageNumber} of ${totalPages}`;
    }
  }

  /**
   * Add footer to a PDF page
   */
  private addFooterToPage(pdf: jsPDF, pageNumber: number, totalPages: number, config: any): void {
    try {
      const footerText = this.generateFooterText(pageNumber, totalPages);

      // Set font for footer
      pdf.setFontSize(7);
      pdf.setTextColor(100, 100, 100); // Gray color for footer

      // Calculate footer position - ensure it's within page bounds
      const footerHeight = this.getFooterHeight(config.contentType, config.orientationType);
      const footerY = Math.max(config.height - config.margin - footerHeight, config.margin + 5);

      // Split footer text into lines
      const lines = footerText.split('\n');
      let currentY = footerY;

      lines.forEach((line, index) => {
        if (line.trim()) {
          let textX: number;

          // Check if this is the page number line (last line)
          if (line.includes('Page') && line.includes('of')) {
            // Right align the page number line
            const textWidth = pdf.getTextWidth(line);
            textX = config.width - config.margin - textWidth;
          } else {
            // Left align all other footer text
            textX = config.margin;
          }

          // Ensure text doesn't go beyond page boundaries
          if (currentY <= config.height - config.margin) {
            pdf.text(line, textX, currentY);
            currentY += 3; // Line spacing
          }
        }
      });
    } catch (error) {
      console.warn('Error adding footer to page:', error);
      // Fallback: add simple footer
      try {
        pdf.setFontSize(7);
        pdf.setTextColor(100, 100, 100);
        const fallbackY = Math.max(config.height - config.margin - 15, config.margin + 5);
        // Right align the fallback page number
        const fallbackText = `Page ${pageNumber} of ${totalPages}`;
        const fallbackWidth = pdf.getTextWidth(fallbackText);
        const fallbackX = config.width - config.margin - fallbackWidth;
        pdf.text(fallbackText, fallbackX, fallbackY);
      } catch (fallbackError) {
        console.error('Failed to add fallback footer:', fallbackError);
      }
    }
  }

  /**
   * Get optimal footer height based on content type and orientation
   */
  private getFooterHeight(contentType?: string, orientation?: string): number {
    // Adjust footer height based on content type and orientation
    let baseHeight = this.footerHeight;

    if (contentType === 'comparison' && orientation === 'landscape') {
      // Landscape comparison pages might need more footer space
      baseHeight = 18;
    } else if (contentType === 'profile') {
      // Profile pages might need less footer space
      baseHeight = 16;
    }

    return baseHeight;
  }

  /**
   * Calculate total PDF pages that will be created from all source elements
   */
  private calculateTotalPDFPages(): number {
    try {
      const pdfPages = document.querySelectorAll('.pdf-page') as NodeListOf<HTMLElement>;
      if (!pdfPages || pdfPages.length === 0) {
        return 1; // Fallback to 1 page if no elements found
      }

      let totalPages = 0;

      for (let i = 0; i < pdfPages.length; i++) {
        const pageElement = pdfPages[i];
        if (!pageElement || !pageElement.getBoundingClientRect) {
          totalPages += 1; // Fallback to 1 page for invalid elements
          continue;
        }

        const isLandscape = pageElement.classList.contains('indexed-interest-pdf-page');
        const config = this.getPDFConfig({
          orientation: isLandscape ? 'landscape' as const : 'portrait' as const
        });

        // Estimate page count based on element dimensions
        const rect = pageElement.getBoundingClientRect();
        if (rect && rect.height > 0) {
          const estimatedHeight = rect.height * (config.scale || 1.5);
          const estimatedHeightMM = estimatedHeight * 0.264583; // Convert px to mm

          if (estimatedHeightMM <= config.contentMaxHeight) {
            totalPages += 1;
          } else {
            const effectivePageHeight = config.contentMaxHeight - (config.overlapBuffer * 2);
            const subPages = Math.ceil(estimatedHeightMM / effectivePageHeight);
            totalPages += subPages;
          }
        } else {
          totalPages += 1; // Fallback to 1 page
        }
      }

      return Math.max(totalPages, 1); // Ensure at least 1 page
    } catch (error) {
      console.warn('Error calculating total PDF pages, using fallback:', error);
      return 1; // Fallback to 1 page on error
    }
  }

  /**
   * Update all footers in the PDF with the correct total page count
   */
  private updateAllFooters(pdf: jsPDF, totalPages: number, config: any): void {
    try {
      const totalPDFPages = pdf.getNumberOfPages();
      console.log(`Updating footers for ${totalPDFPages} pages with total: ${totalPages}`);

      for (let pageNum = 1; pageNum <= totalPDFPages; pageNum++) {
        // Clear the existing footer area
        this.clearFooterArea(pdf, pageNum, config);

        // Add the updated footer
        this.addFooterToPage(pdf, pageNum, totalPages, config);
      }

      console.log(`All footers updated successfully`);
    } catch (error) {
      console.error('Error updating footers:', error);
    }
  }

  /**
   * Clear the footer area on a specific page
   */
  private clearFooterArea(pdf: jsPDF, pageNumber: number, config: any): void {
    try {
      // Set the page as active
      pdf.setPage(pageNumber);

      // Calculate footer area dimensions
      const footerHeight = this.getFooterHeight(config.contentType, config.orientationType);
      const footerY = Math.max(config.height - config.margin - footerHeight, config.margin + 5);

      // Fill footer area with white to clear it
      pdf.setFillColor(255, 255, 255);
      pdf.rect(config.margin, footerY - 5, config.width - (2 * config.margin), footerHeight + 10, 'F');

    } catch (error) {
      console.warn('Error clearing footer area:', error);
    }
  }

  /**
   * Main PDF generation method - handles all content types with unified approach
   * 
   * @param options PDF generation options
   * @returns Promise<void>
   */
  async generatePDF(options: PDFOptions = {}, isComparisonPDF: boolean = false): Promise<void> {
    if (this.isGenerating) {
      console.warn('PDF generation already in progress');
      return;
    }

    try {
      this.isGenerating = true;
      this.isCancelled = false; // Reset cancellation flag
      this.onShowLoader(true);
      this.emitProgress(0, 0, 'Initializing PDF generation...', 0);

      // Find all elements with class "pdf-page"
      const pdfPages = document.querySelectorAll('.pdf-page') as NodeListOf<HTMLElement>;

      if (pdfPages.length === 0) {
        console.warn('No elements with class "pdf-page" found');
        this.onShowLoader(false);
        return;
      }

      //  console.log(`Starting PDF generation for ${pdfPages.length} pages...`);
      this.emitProgress(0, pdfPages.length, 'Processing pages...', 5);

      // Check if we have mixed orientations (portrait and landscape pages)
      const hasLandscapePages = Array.from(pdfPages).some(page =>
        page.classList.contains('indexed-interest-pdf-page')
      );
      const hasPortraitPages = Array.from(pdfPages).some(page =>
        !page.classList.contains('indexed-interest-pdf-page')
      );

      // If we have mixed orientations, we need to process them separately
      if (hasLandscapePages && hasPortraitPages) {
        await this.generateMixedOrientationPDF(pdfPages, options);
        return;
      }

      // Determine orientation based on the first page or options
      const firstPageLandscape = pdfPages[0].classList.contains('indexed-interest-pdf-page');
      const overrideOptions = firstPageLandscape ? { ...options, orientation: 'landscape' as const } : options;

      // Configure PDF document based on content type
      const config = this.getPDFConfig(overrideOptions);
      const pdf = new jsPDF(config.orientation as any, 'mm', 'a4');
      pdf.deletePage(1); // Remove the default blank page

      // We'll track the actual PDF pages as they're created
      let totalPDFPages = 0;
      console.log(`Starting PDF generation for ${pdfPages.length} source elements`);

      // Process each .pdf-page element
      for (let i = 0; i < pdfPages.length; i++) {
        // Check for cancellation before processing each page
        if (this.isCancelled) {
          //  console.log('PDF generation cancelled by user');
          return;
        }

        const pageElement = pdfPages[i];
        const progressPercentage = 10 + ((i / pdfPages.length) * 80);
        this.emitProgress(i + 1, pdfPages.length, `Processing page ${i + 1}...`, progressPercentage);

        //  console.log(`Processing page ${i + 1}/${pdfPages.length}...`);

        // Store original styles for restoration
        const originalStyles = this.storeElementStyles(pageElement);

        try {
          // Debug: Log element info before preparation
          this.logElementDebugInfo(pageElement, `Before Preparation - Page ${i + 1}`);

          // Prepare element for PDF capture
          await this.prepareElementForPDF(pageElement, options.contentType || 'general');

          // Check for cancellation after preparation
          if (this.isCancelled) {
            this.restoreElementStyles(pageElement, originalStyles);
            //  console.log('PDF generation cancelled during page preparation');
            return;
          }

          // Debug: Log element info after preparation
          this.logElementDebugInfo(pageElement, `After Preparation - Page ${i + 1}`);

          // Allow time for layout stabilization
          await this.waitForNextFrame();

          // Additional stabilization time for complex content
          await this.delay(300);

          // Check for cancellation before canvas capture
          if (this.isCancelled) {
            this.restoreElementStyles(pageElement, originalStyles);
            //  console.log('PDF generation cancelled before canvas capture');
            return;
          }

          // Capture element with html2canvas
          const canvas = await this.captureElementAsCanvas(pageElement, config);

          // Check for cancellation after canvas capture
          if (this.isCancelled) {
            this.restoreElementStyles(pageElement, originalStyles);
            if (canvas) {
              this.cleanupCanvas(canvas);
            }
            //  console.log('PDF generation cancelled after canvas capture');
            return;
          }

          if (canvas && canvas.width > 0 && canvas.height > 0) {
            //  console.log(`Canvas captured successfully: ${canvas.width}x${canvas.height} for page ${i + 1}`);

            // Add pages to PDF with total page count for footer numbering
            await this.addCanvasToPDF(pdf, canvas, config, i + 1, totalPDFPages);

            // Clean up canvas to free memory
            this.cleanupCanvas(canvas);
          } else {
            console.warn(`Failed to generate canvas for page ${i + 1}`);
            console.warn(`Canvas dimensions: ${canvas?.width || 'undefined'}x${canvas?.height || 'undefined'}`);
          }
        } catch (error) {
          console.error(`Error processing page ${i + 1}:`, error);
        } finally {
          // Restore original element styles
          this.restoreElementStyles(pageElement, originalStyles);
        }

        // Yield control to prevent UI blocking
        await this.waitForNextFrame();

        // Force garbage collection if available
        if ((window as any).gc) {
          (window as any).gc();
        }
      }

      // Final check for cancellation before saving
      if (this.isCancelled) {
        //  console.log('PDF generation cancelled before saving');
        return;
      }

      this.emitProgress(pdfPages.length, pdfPages.length, 'Finalizing PDF...', 95);

      // Update all footers with the correct total page count
      const finalTotalPages = pdf.getNumberOfPages();
      console.log(`Final PDF has ${finalTotalPages} pages - updating all footers`);
      this.updateAllFooters(pdf, finalTotalPages, config);

      // Generate filename and trigger download
      const filename = options.filename || `PDF_Export_${new Date().getTime()}.pdf`;
      pdf.save(filename);

      this.emitProgress(pdfPages.length, pdfPages.length, 'PDF Generated Successfully!', 100);
      //  console.log(`PDF generation completed: ${filename}`);

    } catch (error) {
      console.error('Error generating PDF:', error);
      this.emitProgress(0, 0, 'Error generating PDF', 0);
      alert('Error generatingPDF. Please try again.');
    } finally {
      this.isGenerating = false;
      this.isCancelled = false; // Reset cancellation flag
      this.onShowLoader(false);
    }
  }

  /**
   * Generate PDF from HTML content with .pdf-page elements
   * Handles automatic page splitting and PrimeNG component protection
   * 
   * @param options PDF generation options
   * @returns Promise<void>
   */
  async generatePdf(options: PDFOptions = {}): Promise<void> {
    if (this.isGenerating) {
      console.warn('PDF generation already in progress');
      return;
    }

    try {
      this.isGenerating = true;
      this.isCancelled = false;
      this.onShowLoader(true);
      this.emitProgress(0, 0, 'Initializing PDF generation...', 0);

      // Find all elements with class "pdf-page"
      const pdfPages = document.querySelectorAll('.pdf-page') as NodeListOf<HTMLElement>;

      if (pdfPages.length === 0) {
        console.warn('No elements with class "pdf-page" found');
        this.onShowLoader(false);
        return;
      }

      console.log(`Starting PDF generation for ${pdfPages.length} .pdf-page elements`);
      this.emitProgress(0, pdfPages.length, 'Processing pages...', 5);

      // Determine orientation based on the first page or options
      const firstPageLandscape = pdfPages[0].classList.contains('landscape');
      const overrideOptions = firstPageLandscape ? { ...options, orientation: 'landscape' as const } : options;

      // Configure PDF document
      const config = this.getPDFConfig(overrideOptions);
      const pdf = new jsPDF(config.orientation as any, 'mm', 'a4');
      pdf.deletePage(1); // Remove the default blank page

      let totalPDFPages = 0;

      // Process each .pdf-page element
      for (let i = 0; i < pdfPages.length; i++) {
        if (this.isCancelled) {
          console.log('PDF generation cancelled by user');
          return;
        }

        const pageElement = pdfPages[i];
        const progressPercentage = 10 + ((i / pdfPages.length) * 80);
        this.emitProgress(i + 1, pdfPages.length, `Processing page ${i + 1}...`, progressPercentage);

        console.log(`Processing .pdf-page ${i + 1}/${pdfPages.length}...`);

        // Store original styles for restoration
        const originalStyles = this.storeElementStyles(pageElement);

        try {
          // Prepare element for PDF capture
          await this.prepareElementForPDF(pageElement, options.contentType || 'general');

          if (this.isCancelled) {
            this.restoreElementStyles(pageElement, originalStyles);
            console.log('PDF generation cancelled during page preparation');
            return;
          }

          // Allow time for layout stabilization
          await this.waitForNextFrame();
          await this.delay(300);

          if (this.isCancelled) {
            this.restoreElementStyles(pageElement, originalStyles);
            console.log('PDF generation cancelled before canvas capture');
            return;
          }

          // Capture element with html2canvas using high quality settings
          const canvas = await this.captureElementAsCanvas(pageElement, config);

          if (this.isCancelled) {
            this.restoreElementStyles(pageElement, originalStyles);
            if (canvas) {
              this.cleanupCanvas(canvas);
            }
            console.log('PDF generation cancelled after canvas capture');
            return;
          }

          if (canvas && canvas.width > 0 && canvas.height > 0) {
            console.log(`Canvas captured successfully: ${canvas.width}x${canvas.height} for page ${i + 1}`);

            // Add pages to PDF with smart page splitting
            await this.addCanvasToPDFWithSmartSplitting(pdf, canvas, config, i + 1);

            // Clean up canvas to free memory
            this.cleanupCanvas(canvas);
          } else {
            console.warn(`Failed to generate canvas for page ${i + 1}`);
          }
        } catch (error) {
          console.error(`Error processing page ${i + 1}:`, error);
        } finally {
          // Restore original element styles
          this.restoreElementStyles(pageElement, originalStyles);
        }

        // Yield control to prevent UI blocking
        await this.waitForNextFrame();

        // Force garbage collection if available
        if ((window as any).gc) {
          (window as any).gc();
        }
      }

      if (this.isCancelled) {
        console.log('PDF generation cancelled before saving');
        return;
      }

      this.emitProgress(pdfPages.length, pdfPages.length, 'Finalizing PDF...', 95);

      // Update all footers with the correct total page count
      const finalTotalPages = pdf.getNumberOfPages();
      console.log(`Final PDF has ${finalTotalPages} pages - updating all footers`);
      this.updateAllFooters(pdf, finalTotalPages, config);

      // Generate filename and trigger download
      const filename = options.filename || `PDF_Export_${new Date().getTime()}.pdf`;
      pdf.save(filename);

      this.emitProgress(pdfPages.length, pdfPages.length, 'PDF Generated Successfully!', 100);
      console.log(`PDF generation completed: ${filename}`);

    } catch (error) {
      console.error('Error generating PDF:', error);
      this.emitProgress(0, 0, 'Error generating PDF', 0);
      alert('Error generating PDF. Please try again.');
    } finally {
      this.isGenerating = false;
      this.isCancelled = false;
      this.onShowLoader(false);
    }
  }

  /**
   * Get PDF configuration based on content type and orientation
   */
  private getPDFConfig(options: PDFOptions) {
    const isComparison = options.contentType === 'comparison';
    const isLandscape = options.orientation === 'landscape' || (isComparison && !options.orientation);

    // Calculate footer height for this configuration
    const footerHeight = this.getFooterHeight(options.contentType, isLandscape ? 'landscape' : 'portrait');

    return {
      orientation: isLandscape ? 'l' : 'p' as 'portrait' | 'landscape',
      width: isLandscape ? 297 : 210,  // A4 landscape vs portrait
      height: isLandscape ? 210 : 297,
      margin: options.margins?.top || (isLandscape ? 5 : 6),
      maxWidth: isLandscape ? 287 : 198,  // width - (2 * margin)
      maxHeight: isLandscape ? 200 : 285, // height - (2 * margin) - footerHeight
      contentMaxHeight: isLandscape ? (200 - footerHeight - (options.margins?.top || 5)) : (285 - footerHeight - (options.margins?.top || 6)),
      overlapBuffer: isLandscape ? 2 : 1.5,
      scale: options.scale || (isLandscape ? 1.8 : 1.5),
      quality: options.quality || 0.90,
      contentType: options.contentType || 'general',
      orientationType: isLandscape ? 'landscape' : 'portrait'
    };
  }

  /**
   * Capture element as canvas with optimized settings and full content height detection
   */
  private async captureElementAsCanvas(element: HTMLElement, config: any): Promise<HTMLCanvasElement> {
    // Calculate the true content dimensions to prevent cutting
    const contentDimensions = this.calculateTrueContentDimensions(element);

    return await html2canvas(element, {
      scale: 2, // High quality rendering as specified
      useCORS: true, // Enable CORS for images as specified
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      removeContainer: true,
      imageTimeout: 15000,
      width: contentDimensions.width,
      height: contentDimensions.height,
      windowWidth: Math.max(contentDimensions.width, config.orientation === 'l' ? 1600 : 1400),
      windowHeight: Math.max(contentDimensions.height, 1000),
      scrollX: 0,
      scrollY: 0,
      x: 0,
      y: 0,
      onclone: (clonedDoc) => {
        // Ensure cloned document renders properly and shows all content
        const clonedElement = clonedDoc.querySelector('.pdf-page') as HTMLElement;
        if (clonedElement) {
          // Force element to show all content
          this.expandElementForFullCapture(clonedElement);

          // Ensure proper visibility
          clonedElement.style.visibility = 'visible';
          clonedElement.style.opacity = '1';
          clonedElement.style.transform = 'none';
          clonedElement.style.position = 'relative';
          clonedElement.style.top = '0';
          clonedElement.style.left = '0';

          // Fix tables in cloned document
          const tables = clonedElement.querySelectorAll('table') as NodeListOf<HTMLElement>;
          tables.forEach(table => {
            table.style.tableLayout = 'fixed';
            table.style.width = '100%';
            table.style.height = 'auto';
            table.style.overflow = 'visible';
          });

          // Fix datatable wrappers for indexed interest comparison
          const datatableWrappers = clonedElement.querySelectorAll('.p-datatable-wrapper') as NodeListOf<HTMLElement>;
          datatableWrappers.forEach(wrapper => {
            wrapper.style.overflow = 'visible';
            wrapper.style.maxHeight = 'none';
            wrapper.style.height = 'auto';
          });

          // Additional fixes for indexed interest comparison nested content
          const innerTables = clonedElement.querySelectorAll('.inner-table') as NodeListOf<HTMLElement>;
          innerTables.forEach(innerTable => {
            innerTable.style.overflow = 'visible';
            innerTable.style.height = 'auto';
            innerTable.style.maxHeight = 'none';
          });
        }
      }
    });
  }

  /**
   * Add canvas content to PDF with smart page splitting
   */
  private async addCanvasToPDF(pdf: jsPDF, canvas: HTMLCanvasElement, config: any, pageNumber: number, totalPages?: number): Promise<void> {
    const canvasAspectRatio = canvas.width / canvas.height;
    const imgWidth = config.maxWidth;
    const imgHeight = imgWidth / canvasAspectRatio;

    // console.log('canvas', canvas.width, canvas.height);
    // console.log('imgWidth', imgWidth, 'imgHeight', imgHeight);
    ////  console.log(`Page ${pageNumber}: Content size ${imgWidth.toFixed(1)}mm x ${imgHeight.toFixed(1)}mm`);

    if (imgHeight <= config.contentMaxHeight) {
      // Content fits on single page with footer space reserved
      pdf.addPage();
      const imgData = canvas.toDataURL('image/png', config.quality);
      // console.log('imgData', imgData);
      pdf.addImage(imgData, 'PNG', config.margin, config.margin, imgWidth, imgHeight);

      // Add footer to this page with actual PDF page number
      const currentPDFPage = pdf.getNumberOfPages();
      // We'll update the footer with the correct total after all pages are created
      // For now, use a placeholder that will be updated later
      this.addFooterToPage(pdf, currentPDFPage, 999, config);
    } else {
      // Content requires multiple pages - use smart splitting
      await this.handlePageSplitting(pdf, canvas, config, pageNumber, totalPages);
    }
  }

  /**
   * Add canvas content to PDF with smart page splitting and PrimeNG component protection
   */
  private async addCanvasToPDFWithSmartSplitting(
    pdf: jsPDF,
    canvas: HTMLCanvasElement,
    config: any,
    pageNumber: number
  ): Promise<void> {
    const canvasAspectRatio = canvas.width / canvas.height;
    const imgWidth = config.maxWidth;
    const imgHeight = imgWidth / canvasAspectRatio;

    console.log(`Page ${pageNumber}: Content size ${imgWidth.toFixed(1)}mm x ${imgHeight.toFixed(1)}mm`);

    if (imgHeight <= config.contentMaxHeight) {
      // Content fits on single page
      pdf.addPage();
      const imgData = canvas.toDataURL('image/png', config.quality);
      pdf.addImage(imgData, 'PNG', config.margin, config.margin, imgWidth, imgHeight);

      // Add footer to this page
      const currentPDFPage = pdf.getNumberOfPages();
      this.addFooterToPage(pdf, currentPDFPage, 999, config);
    } else {
      // Content requires multiple pages - use smart splitting with PrimeNG protection
      await this.handleSmartPageSplitting(pdf, canvas, config, pageNumber);
    }
  }

  /**
   * Handle multi-page content splitting with overlap prevention and content protection
   */
  private async handlePageSplitting(pdf: jsPDF, canvas: HTMLCanvasElement, config: any, pageNumber: number, totalPDFPages?: number): Promise<void> {
    const canvasHeight = canvas.height;
    const imgWidth = config.maxWidth;
    const imgHeight = imgWidth / (canvas.width / canvas.height);

    // Use smaller effective page height to ensure no content cutting and reserve footer space
    const effectivePageHeight = config.contentMaxHeight - (config.overlapBuffer * 2);
    const subPages = Math.ceil(imgHeight / effectivePageHeight);

    //  console.log(`Page ${pageNumber}: Requires ${subPages} sub-pages (${imgHeight.toFixed(1)}mm total height, canvas: ${canvasHeight}px)`);

    let processedHeight = 0;

    for (let subPage = 0; subPage < subPages; subPage++) {
      // Check for cancellation before processing each sub-page
      if (this.isCancelled) {
        //  console.log('PDF generation cancelled during page splitting');
        return;
      }

      pdf.addPage();

      // Calculate source area with enhanced content protection
      const { sourceY, sourceHeight } = this.calculatePageSourceProtected(
        subPage, subPages, canvasHeight, imgHeight, effectivePageHeight, config.overlapBuffer, processedHeight
      );

      // Validate that we're not cutting content
      const remainingCanvasHeight = canvasHeight - sourceY;
      const actualSourceHeight = Math.min(sourceHeight, remainingCanvasHeight);

      if (actualSourceHeight <= 0) {
        console.warn(`Sub-page ${subPage + 1}: No content remaining, skipping`);
        break;
      }

      // Create page canvas for this section
      const pageCanvas = this.createPageCanvasProtected(canvas, sourceY, actualSourceHeight);
      const pageImgHeight = Math.min((pageCanvas.height * imgWidth) / canvas.width, config.contentMaxHeight);
      const pageImgData = pageCanvas.toDataURL('image/png', config.quality);

      // Add to PDF
      pdf.addImage(pageImgData, 'PNG', config.margin, config.margin, imgWidth, pageImgHeight);

      // Add footer to this sub-page with proper page numbering
      // For sub-pages, we need to calculate the actual PDF page number
      const currentPDFPage = pdf.getNumberOfPages();
      // We'll update the footer with the correct total after all pages are created
      this.addFooterToPage(pdf, currentPDFPage, 999, config);

      // Track processed height
      processedHeight += (actualSourceHeight * imgHeight) / canvasHeight;

      // Clean up page canvas
      this.cleanupCanvas(pageCanvas);

      // Update progress for sub-pages
      const subPageProgress = ((subPage + 1) / subPages) * 5;
      this.emitProgress(pageNumber, 0, `Processing sub-page ${subPage + 1}/${subPages}...`, 10 + subPageProgress);

      // Yield control
      await this.waitForNextFrame();

      // Check for cancellation after yielding control
      if (this.isCancelled) {
        //  console.log('PDF generation cancelled during sub-page processing');
        return;
      }

      // Check if we've processed all content
      if (sourceY + actualSourceHeight >= canvasHeight) {
        break;
      }
    }
  }

  /**
   * Handle smart page splitting with PrimeNG component protection
   */
  private async handleSmartPageSplitting(
    pdf: jsPDF,
    canvas: HTMLCanvasElement,
    config: any,
    pageNumber: number
  ): Promise<void> {
    const canvasHeight = canvas.height;
    const imgWidth = config.maxWidth;
    const imgHeight = imgWidth / (canvas.width / canvas.height);

    // Use smaller effective page height to ensure no content cutting
    const effectivePageHeight = config.contentMaxHeight - (config.overlapBuffer * 2);
    const subPages = Math.ceil(imgHeight / effectivePageHeight);

    console.log(`Page ${pageNumber}: Requires ${subPages} sub-pages (${imgHeight.toFixed(1)}mm total height)`);

    let processedHeight = 0;

    for (let subPage = 0; subPage < subPages; subPage++) {
      if (this.isCancelled) {
        console.log('PDF generation cancelled during smart page splitting');
        return;
      }

      pdf.addPage();

      // Calculate source area with enhanced content protection
      const { sourceY, sourceHeight } = this.calculateSmartPageSource(
        subPage, subPages, canvasHeight, imgHeight, effectivePageHeight, config.overlapBuffer, processedHeight
      );

      // Validate that we're not cutting content
      const remainingCanvasHeight = canvasHeight - sourceY;
      const actualSourceHeight = Math.min(sourceHeight, remainingCanvasHeight);

      if (actualSourceHeight <= 0) {
        console.warn(`Sub-page ${subPage + 1}: No content remaining, skipping`);
        break;
      }

      // Create page canvas for this section
      const pageCanvas = this.createPageCanvasProtected(canvas, sourceY, actualSourceHeight);
      const pageImgHeight = Math.min((pageCanvas.height * imgWidth) / canvas.width, config.contentMaxHeight);
      const pageImgData = pageCanvas.toDataURL('image/png', config.quality);

      // Add to PDF
      pdf.addImage(pageImgData, 'PNG', config.margin, config.margin, imgWidth, pageImgHeight);

      // Add footer to this sub-page
      const currentPDFPage = pdf.getNumberOfPages();
      this.addFooterToPage(pdf, currentPDFPage, 999, config);

      // Track processed height
      processedHeight += (actualSourceHeight * imgHeight) / canvasHeight;

      // Clean up page canvas
      this.cleanupCanvas(pageCanvas);

      // Update progress for sub-pages
      const subPageProgress = ((subPage + 1) / subPages) * 5;
      this.emitProgress(pageNumber, 0, `Processing sub-page ${subPage + 1}/${subPages}...`, 10 + subPageProgress);

      // Yield control
      await this.waitForNextFrame();

      if (this.isCancelled) {
        console.log('PDF generation cancelled during sub-page processing');
        return;
      }

      // Check if we've processed all content
      if (sourceY + actualSourceHeight >= canvasHeight) {
        break;
      }
    }
  }

  /**
   * Calculate page source dimensions for splitting
   */
  private calculatePageSource(
    subPage: number,
    totalPages: number,
    canvasHeight: number,
    imgHeight: number,
    effectivePageHeight: number,
    overlapBuffer: number
  ): { sourceY: number; sourceHeight: number } {
    let sourceY: number;
    let sourceHeight: number;

    if (subPage === 0) {
      // First page: start from top
      sourceY = 0;
      sourceHeight = Math.min((effectivePageHeight * canvasHeight) / imgHeight, canvasHeight);
    } else if (subPage === totalPages - 1) {
      // Last page: ensure all remaining content is captured
      const remainingHeight = canvasHeight - ((subPage * effectivePageHeight * canvasHeight) / imgHeight);
      sourceHeight = remainingHeight;
      sourceY = canvasHeight - sourceHeight;

      // Ensure we don't overlap with previous page
      const minSourceY = ((subPage * effectivePageHeight * canvasHeight) / imgHeight) - (overlapBuffer * canvasHeight / imgHeight);
      sourceY = Math.max(sourceY, minSourceY);
      sourceHeight = canvasHeight - sourceY;
    } else {
      // Middle pages: include small overlap to prevent content cutoff
      const baseSourceY = (subPage * effectivePageHeight * canvasHeight) / imgHeight;
      sourceY = baseSourceY - (overlapBuffer * canvasHeight / imgHeight / 2);
      sourceHeight = (effectivePageHeight * canvasHeight) / imgHeight + (overlapBuffer * canvasHeight / imgHeight);

      sourceY = Math.max(0, sourceY);
      sourceHeight = Math.min(sourceHeight, canvasHeight - sourceY);
    }

    return { sourceY, sourceHeight };
  }

  /**
   * Calculate page source dimensions with enhanced protection against content cutting
   */
  private calculatePageSourceProtected(
    subPage: number,
    totalPages: number,
    canvasHeight: number,
    imgHeight: number,
    effectivePageHeight: number,
    overlapBuffer: number,
    processedHeight: number
  ): { sourceY: number; sourceHeight: number } {
    let sourceY: number;
    let sourceHeight: number;

    // Calculate the starting position based on processed height
    const heightPerPage = effectivePageHeight;

    if (subPage === 0) {
      // First page: start from top
      sourceY = 0;
      sourceHeight = Math.min((heightPerPage * canvasHeight) / imgHeight, canvasHeight);
    } else {
      // Calculate position based on actual processed content
      sourceY = (processedHeight * canvasHeight) / imgHeight;

      // Ensure we don't start beyond the canvas
      sourceY = Math.min(sourceY, canvasHeight - 1);

      if (subPage === totalPages - 1) {
        // Last page: capture all remaining content
        sourceHeight = canvasHeight - sourceY;
      } else {
        // Middle pages: use effective page height but ensure no cutting
        sourceHeight = Math.min(
          (heightPerPage * canvasHeight) / imgHeight,
          canvasHeight - sourceY
        );
      }
    }

    // Add small buffer to prevent content cutting at boundaries
    if (subPage > 0 && sourceY > overlapBuffer) {
      sourceY -= overlapBuffer;
      sourceHeight += overlapBuffer;
    }

    // Ensure we don't exceed canvas bounds
    sourceY = Math.max(0, Math.min(sourceY, canvasHeight));
    sourceHeight = Math.max(1, Math.min(sourceHeight, canvasHeight - sourceY));

    return { sourceY, sourceHeight };
  }

  /**
   * Calculate smart page source dimensions with PrimeNG component protection
   */
  private calculateSmartPageSource(
    subPage: number,
    totalPages: number,
    canvasHeight: number,
    imgHeight: number,
    effectivePageHeight: number,
    overlapBuffer: number,
    processedHeight: number
  ): { sourceY: number; sourceHeight: number } {
    let sourceY: number;
    let sourceHeight: number;

    // Calculate the starting position based on processed height
    const heightPerPage = effectivePageHeight;

    if (subPage === 0) {
      // First page: start from top
      sourceY = 0;
      sourceHeight = Math.min((heightPerPage * canvasHeight) / imgHeight, canvasHeight);
    } else {
      // Calculate position based on actual processed content
      sourceY = (processedHeight * canvasHeight) / imgHeight;

      // Ensure we don't start beyond the canvas
      sourceY = Math.min(sourceY, canvasHeight - 1);

      if (subPage === totalPages - 1) {
        // Last page: capture all remaining content
        sourceHeight = canvasHeight - sourceY;
      } else {
        // Middle pages: use effective page height but ensure no cutting
        sourceHeight = Math.min(
          (heightPerPage * canvasHeight) / imgHeight,
          canvasHeight - sourceY
        );
      }
    }

    // Add small buffer to prevent content cutting at boundaries
    if (subPage > 0 && sourceY > overlapBuffer) {
      sourceY -= overlapBuffer;
      sourceHeight += overlapBuffer;
    }

    // Ensure we don't exceed canvas bounds
    sourceY = Math.max(0, Math.min(sourceY, canvasHeight));
    sourceHeight = Math.max(1, Math.min(sourceHeight, canvasHeight - sourceY));

    return { sourceY, sourceHeight };
  }

  /**
   * Protect PrimeNG components from being split across pages
   */
  private protectPrimeNGComponents(element: HTMLElement): void {
    // Protect PrimeNG panels from page breaks
    const panels = element.querySelectorAll('.p-panel, .p-panel-content') as NodeListOf<HTMLElement>;
    panels.forEach(panel => {
      panel.style.pageBreakInside = 'avoid';
      panel.style.breakInside = 'avoid';
      panel.style.orphans = '3';
      panel.style.widows = '3';
    });

    // Protect PrimeNG accordion tabs from page breaks
    const accordionTabs = element.querySelectorAll('.p-accordion-tab, .p-accordion-content') as NodeListOf<HTMLElement>;
    accordionTabs.forEach(tab => {
      tab.style.pageBreakInside = 'avoid';
      tab.style.breakInside = 'avoid';
      tab.style.orphans = '3';
      tab.style.widows = '3';
    });

    // Protect PrimeNG datatables from page breaks
    const datatables = element.querySelectorAll('.p-datatable, .p-datatable-wrapper') as NodeListOf<HTMLElement>;
    datatables.forEach(datatable => {
      datatable.style.pageBreakInside = 'avoid';
      datatable.style.breakInside = 'avoid';
    });

    // Protect PrimeNG table rows from page breaks
    const tableRows = element.querySelectorAll('.p-datatable tbody tr') as NodeListOf<HTMLElement>;
    tableRows.forEach(row => {
      row.style.pageBreakInside = 'avoid';
      row.style.breakInside = 'avoid';
    });

    // Protect PrimeNG cards from page breaks
    const cards = element.querySelectorAll('.p-card, .p-card-body') as NodeListOf<HTMLElement>;
    cards.forEach(card => {
      card.style.pageBreakInside = 'avoid';
      card.style.breakInside = 'avoid';
    });

    // Protect PrimeNG fieldset from page breaks
    const fieldsets = element.querySelectorAll('.p-fieldset, .p-fieldset-content') as NodeListOf<HTMLElement>;
    fieldsets.forEach(fieldset => {
      fieldset.style.pageBreakInside = 'avoid';
      fieldset.style.breakInside = 'avoid';
    });

    // Protect PrimeNG tabview content from page breaks
    const tabPanels = element.querySelectorAll('.p-tabview-panel, .p-tabview-content') as NodeListOf<HTMLElement>;
    tabPanels.forEach(panel => {
      panel.style.pageBreakInside = 'avoid';
      panel.style.breakInside = 'avoid';
    });

    // Handle section header rows to prevent them from appearing at page breaks
    this.handleSectionHeaderPageBreaks(element);

    // Add CSS classes for additional page break protection
    element.classList.add('pdf-page-break-protected');

    // Add CSS rules to prevent page breaks within components
    const style = document.createElement('style');
    style.textContent = `
      .pdf-page-break-protected .p-panel,
      .pdf-page-break-protected .p-panel-content,
      .pdf-page-break-protected .p-accordion-tab,
      .pdf-page-break-protected .p-accordion-content,
      .pdf-page-break-protected .p-datatable,
      .pdf-page-break-protected .p-datatable-wrapper,
      .pdf-page-break-protected .p-datatable tbody tr,
      .pdf-page-break-protected .p-card,
      .pdf-page-break-protected .p-card-body,
      .pdf-page-break-protected .p-fieldset,
      .pdf-page-break-protected .p-fieldset-content,
      .pdf-page-break-protected .p-tabview-panel,
      .pdf-page-break-protected .p-tabview-content {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
        orphans: 3 !important;
        widows: 3 !important;
      }
      
      /* Section header row page break protection - specifically for section-header-row class */
      .pdf-page-break-protected tr.section-header-row {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
        orphans: 3 !important;
        widows: 3 !important;
      }
      
      /* Force page break for section-header-row at page end */
      .pdf-page-break-protected tr.section-header-row.force-page-break-before {
        page-break-before: always !important;
        break-before: always !important;
        margin-top: 20px !important;
      }
      
      /* General section header protection */
      .pdf-page-break-protected .section-header-row,
      .pdf-page-break-protected .section-header {
        page-break-before: avoid !important;
        break-before: avoid !important;
        page-break-after: avoid !important;
        break-after: avoid !important;
        page-break-inside: avoid !important;
        break-inside: avoid !important;
        orphans: 3 !important;
        widows: 3 !important;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Handle section header rows to prevent them from appearing at page breaks
   * This method ensures section headers are pushed to the next page if they would appear at the end
   */
  private handleSectionHeaderPageBreaks(element: HTMLElement): void {
    // Find all section header rows
    const sectionHeaderRows = element.querySelectorAll('.section-header-row, tr.section-header-row, .section-header') as NodeListOf<HTMLElement>;
    
    sectionHeaderRows.forEach((headerRow, index) => {
      // Add specific CSS classes for section header protection
      headerRow.classList.add('section-header-protected');
      
      // Add page break before to ensure it starts on a new page if needed
      headerRow.style.pageBreakBefore = 'auto';
      headerRow.style.breakBefore = 'auto';
      
      // Add data attribute for JavaScript detection
      headerRow.setAttribute('data-section-header', 'true');
      headerRow.setAttribute('data-header-index', index.toString());
      
      // Add a wrapper div to help with page break detection
      if (!headerRow.parentElement?.classList.contains('section-header-wrapper')) {
        const wrapper = document.createElement('div');
        wrapper.className = 'section-header-wrapper';
        wrapper.style.pageBreakInside = 'avoid';
        wrapper.style.breakInside = 'avoid';
        
        // Insert wrapper before the header row
        headerRow.parentNode?.insertBefore(wrapper, headerRow);
        // Move header row into wrapper
        wrapper.appendChild(headerRow);
      }
    });

    // Add CSS for section header protection
    const sectionStyle = document.createElement('style');
    sectionStyle.textContent = `
      .section-header-wrapper {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
        margin-bottom: 0 !important;
        padding-bottom: 0 !important;
      }
      
      .section-header-protected {
        page-break-before: auto !important;
        break-before: auto !important;
        page-break-after: avoid !important;
        break-after: avoid !important;
        page-break-inside: avoid !important;
        break-inside: avoid !important;
        orphans: 3 !important;
        widows: 3 !important;
      }
      
      /* Specific protection for section-header-row class */
      tr.section-header-row {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
        orphans: 3 !important;
        widows: 3 !important;
      }
      
      /* Ensure section headers don't appear alone at the end of a page */
      .section-header-protected:last-child,
      tr.section-header-row:last-child {
        page-break-before: always !important;
        break-before: always !important;
      }
      
      /* Force page break for section-header-row at page end */
      tr.section-header-row.force-page-break-before {
        page-break-before: always !important;
        break-before: always !important;
        margin-top: 20px !important;
      }
    `;
    document.head.appendChild(sectionStyle);
  }

  /**
   * Advanced method to detect and handle section headers at page breaks during PDF generation
   * This method works with canvas-based PDF generation by adding spacing and visual breaks
   */
  private analyzeAndFixSectionHeaderBreaks(element: HTMLElement): void {
    // Get all tables in the element
    const tables = element.querySelectorAll('table, .p-datatable') as NodeListOf<HTMLElement>;
    
    tables.forEach(table => {
      const rows = table.querySelectorAll('tr') as NodeListOf<HTMLElement>;
      const sectionHeaderRows: HTMLElement[] = [];
      
      // Specifically identify tr elements with 'section-header-row' class
      rows.forEach((row, index) => {
        if (row.classList.contains('section-header-row')) {
          sectionHeaderRows.push(row);
        }
      });
      
      // Process each section header row
      sectionHeaderRows.forEach((headerRow, headerIndex) => {
        const rowIndex = Array.from(rows).indexOf(headerRow);
        const isLastRow = rowIndex === rows.length - 1;
        const isNearEnd = rowIndex >= rows.length - 3; // Within last 3 rows
        
        // Calculate available space considering footer and margins
        const availableSpace = this.calculateAvailableSpaceForRow(headerRow, element);
        const rowHeight = this.estimateRowHeight(headerRow);
        
        // Check if section header would appear too close to page end
        const wouldAppearAtPageEnd = this.wouldRowAppearAtPageEnd(headerRow, availableSpace, rowHeight);
        
        // If section header is at or near the end, or would appear at page end, add spacing
        if (isLastRow || isNearEnd || wouldAppearAtPageEnd) {
          this.addPageBreakSpacing(headerRow, 'before');
          headerRow.classList.add('force-page-break-before');
          
          // Add visual indicator for debugging
          let reason = 'unknown';
          if (isLastRow) reason = 'last-row';
          else if (isNearEnd) reason = 'near-end';
          else if (wouldAppearAtPageEnd) reason = 'page-end-detected';
          
          headerRow.setAttribute('data-page-break-reason', reason);
          console.log(`Section header row "${headerRow.textContent?.trim()}" will have page break spacing (reason: ${reason})`);
        }
        
        // Check if section header has minimal content after it
        const nextRows = Array.from(rows).slice(rowIndex + 1);
        const hasMinimalContent = nextRows.length <= 2;
        
        if (hasMinimalContent && nextRows.length > 0) {
          // Add spacing to push section header to next page
          this.addPageBreakSpacing(headerRow, 'before');
          headerRow.classList.add('force-page-break-before');
          headerRow.setAttribute('data-page-break-reason', 'minimal-content-after');
          console.log(`Section header row "${headerRow.textContent?.trim()}" will have page break spacing (reason: minimal-content-after)`);
        }
      });
    });

    // Also handle h6 elements (case headers) with content threshold logic
    this.handleH6ElementPageBreaks(element);
  }

  /**
   * Handle h6 elements (case headers) with content threshold logic
   * Only moves h6 to next page if there's minimal content after it
   */
  private handleH6ElementPageBreaks(element: HTMLElement): void {
    const h6Elements = element.querySelectorAll('div.h6') as NodeListOf<HTMLElement>;
    
    h6Elements.forEach((h6Element, index) => {
      // Check if there's substantial content after this h6 element
      const hasSubstantialContentAfter = this.hasSubstantialContentAfterElement(h6Element);
      
      if (!hasSubstantialContentAfter) {
        // Only add page break if there's minimal content after the h6
        const isNearPageEnd = this.isElementNearPageEnd(h6Element, element);
        
        if (isNearPageEnd) {
          this.addPageBreakSpacing(h6Element, 'before');
          h6Element.classList.add('force-page-break-before');
          h6Element.setAttribute('data-page-break-reason', 'h6-minimal-content-after');
          console.log(`H6 element "${h6Element.textContent?.trim()}" will have page break spacing (reason: minimal content after)`);
        }
      } else {
        console.log(`H6 element "${h6Element.textContent?.trim()}" will stay on current page (reason: substantial content after)`);
      }
    });
  }

  /**
   * Check if there's substantial content after an element
   */
  private hasSubstantialContentAfterElement(element: HTMLElement): boolean {
    const nextSiblings = this.getNextSiblings(element);
    let contentHeight = 0;
    let elementCount = 0;
    
    // Calculate content height and element count after the element
    nextSiblings.forEach(sibling => {
      if (sibling.nodeType === Node.ELEMENT_NODE) {
        const rect = (sibling as HTMLElement).getBoundingClientRect();
        contentHeight += rect.height;
        elementCount++;
        
        // Check for nested content (tables, divs, etc.)
        const nestedElements = (sibling as HTMLElement).querySelectorAll('table, div, tr, td, p, span');
        elementCount += nestedElements.length;
      }
    });
    
    // Consider substantial if:
    // - More than 200px of content height, OR
    // - More than 5 elements, OR
    // - Contains tables or complex structures
    const hasTables = Array.from(nextSiblings).some(sibling => 
      sibling.nodeType === Node.ELEMENT_NODE && 
      (sibling as HTMLElement).querySelector('table, .p-datatable')
    );
    
    return contentHeight > 200 || elementCount > 5 || hasTables;
  }

  /**
   * Get all next siblings of an element
   */
  private getNextSiblings(element: HTMLElement): Node[] {
    const siblings: Node[] = [];
    let nextSibling = element.nextSibling;
    
    while (nextSibling) {
      siblings.push(nextSibling);
      nextSibling = nextSibling.nextSibling;
    }
    
    return siblings;
  }

  /**
   * Check if an element is near the end of the page
   */
  private isElementNearPageEnd(element: HTMLElement, container: HTMLElement): boolean {
    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    
    // Get footer height
    const footerHeight = this.getFooterHeight('comparison');
    const footerHeightPx = footerHeight * 3.779527559;
    const availableSpace = containerRect.height - footerHeightPx - 40; // 40px margin buffer
    
    // Consider near page end if within 150px of available space
    return elementRect.bottom > (availableSpace - 150);
  }

  /**
   * Add visual spacing to simulate page breaks for canvas-based PDF generation
   */
  private addPageBreakSpacing(element: HTMLElement, position: 'before' | 'after'): void {
    // Create a page break spacer element
    const spacer = document.createElement('div');
    spacer.className = 'page-break-spacer';
    spacer.style.height = '600px'; // Approximate page height minus margins
    spacer.style.width = '100%';
    spacer.style.backgroundColor = 'transparent';
    spacer.style.border = 'none';
    spacer.style.margin = '0';
    spacer.style.padding = '0';
    spacer.style.pageBreakInside = 'avoid';
    spacer.style.breakInside = 'avoid';
    
    // Add data attribute for identification
    spacer.setAttribute('data-page-break-spacer', 'true');
    spacer.setAttribute('data-target-element', 'section-header-row');
    
    if (position === 'before') {
      // Insert spacer before the element
      element.parentNode?.insertBefore(spacer, element);
    } else {
      // Insert spacer after the element
      element.parentNode?.insertBefore(spacer, element.nextSibling);
    }
  }

  /**
   * Calculate available space for a row considering footer and margins
   */
  private calculateAvailableSpaceForRow(row: HTMLElement, container: HTMLElement): number {
    const containerRect = container.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    
    // Get footer height from the service
    const footerHeight = this.getFooterHeight('comparison'); // Default to comparison
    const footerHeightPx = footerHeight * 3.779527559; // Convert mm to px
    
    // Calculate available space (container height - footer - margins)
    const availableHeight = containerRect.height - footerHeightPx - 40; // 40px margin buffer
    
    return availableHeight;
  }

  /**
   * Estimate the height of a table row
   */
  private estimateRowHeight(row: HTMLElement): number {
    const rect = row.getBoundingClientRect();
    return rect.height || 30; // Default to 30px if height is 0
  }

  /**
   * Check if a row would appear at the end of a page considering footer and margins
   */
  private wouldRowAppearAtPageEnd(row: HTMLElement, availableSpace: number, rowHeight: number): boolean {
    const rowRect = row.getBoundingClientRect();
    const rowBottom = rowRect.bottom;
    
    // Consider the row would appear at page end if it's within 100px of the available space limit
    const threshold = 100; // pixels
    const spaceThreshold = availableSpace - threshold;
    
    return rowBottom > spaceThreshold;
  }

  /**
   * Apply page break fixes for section headers before PDF generation
   * This method should be called before the main PDF generation process
   */
  private applySectionHeaderPageBreakFixes(element: HTMLElement): void {
    // First, apply the basic protection
    this.handleSectionHeaderPageBreaks(element);
    
    // Then, analyze and fix specific cases
    this.analyzeAndFixSectionHeaderBreaks(element);
    
    // Add additional CSS for forced page breaks
    const forceBreakStyle = document.createElement('style');
    forceBreakStyle.textContent = `
      .force-page-break-before {
        page-break-before: always !important;
        break-before: always !important;
        margin-top: 20px !important;
      }
      
      /* Ensure section headers with minimal content after them start on new page */
      tr[data-page-break-reason="minimal-content-after"],
      tr[data-page-break-reason="last-row"],
      tr[data-page-break-reason="near-end"] {
        page-break-before: always !important;
        break-before: always !important;
      }
      
      /* Add spacing before section headers that are forced to new page */
      .force-page-break-before::before {
        content: "";
        display: block;
        height: 10px;
        page-break-inside: avoid;
        break-inside: avoid;
      }
    `;
    document.head.appendChild(forceBreakStyle);
  }

  /**
   * Public method to manually fix section header page breaks
   * This can be called from components before PDF generation
   */
  public fixSectionHeaderPageBreaks(elementId?: string): void {
    const element = elementId ? document.getElementById(elementId) : document.body;
    if (element) {
      this.applySectionHeaderPageBreakFixes(element as HTMLElement);
      console.log('Section header page break fixes applied');
    } else {
      console.warn('Element not found for section header page break fixes');
    }
  }

  /**
   * Specific method to fix section-header-row elements at page breaks
   * This method works with canvas-based PDF generation by adding visual spacing
   */
  public fixSectionHeaderRowPageBreaks(elementId?: string): void {
    const element = elementId ? document.getElementById(elementId) : document.body;
    if (!element) {
      console.warn('Element not found for section header row page break fixes');
      return;
    }

    // Find all tr.section-header-row elements
    const sectionHeaderRows = element.querySelectorAll('tr.section-header-row') as NodeListOf<HTMLElement>;
    
    console.log(`Found ${sectionHeaderRows.length} section header rows to process`);

    sectionHeaderRows.forEach((headerRow, index) => {
      // Add protection classes
      headerRow.classList.add('section-header-protected');
      headerRow.setAttribute('data-section-header-row', 'true');
      headerRow.setAttribute('data-header-index', index.toString());

      // Get the table containing this row
      const table = headerRow.closest('table, .p-datatable');
      if (table) {
        const rows = table.querySelectorAll('tr') as NodeListOf<HTMLElement>;
        const rowIndex = Array.from(rows).indexOf(headerRow);
        const isLastRow = rowIndex === rows.length - 1;
        const isNearEnd = rowIndex >= rows.length - 3;

        // Calculate available space considering footer
        const containerRect = element.getBoundingClientRect();
        const rowRect = headerRow.getBoundingClientRect();
        const footerHeight = this.getFooterHeight('comparison');
        const footerHeightPx = footerHeight * 3.779527559;
        const availableSpace = containerRect.height - footerHeightPx - 40; // 40px margin buffer
        const wouldAppearAtPageEnd = rowRect.bottom > (availableSpace - 100);

        // Apply page break logic using visual spacing
        if (isLastRow || isNearEnd || wouldAppearAtPageEnd) {
          this.addPageBreakSpacing(headerRow, 'before');
          headerRow.classList.add('force-page-break-before');

          let reason = 'unknown';
          if (isLastRow) reason = 'last-row';
          else if (isNearEnd) reason = 'near-end';
          else if (wouldAppearAtPageEnd) reason = 'page-end-detected';

          headerRow.setAttribute('data-page-break-reason', reason);
          console.log(`Section header row "${headerRow.textContent?.trim()}" will have page break spacing (reason: ${reason})`);
        }

        // Check for minimal content after section header
        const nextRows = Array.from(rows).slice(rowIndex + 1);
        const hasMinimalContent = nextRows.length <= 2;

        if (hasMinimalContent && nextRows.length > 0) {
          this.addPageBreakSpacing(headerRow, 'before');
          headerRow.classList.add('force-page-break-before');
          headerRow.setAttribute('data-page-break-reason', 'minimal-content-after');
          console.log(`Section header row "${headerRow.textContent?.trim()}" will have page break spacing (reason: minimal-content-after)`);
        }
      }
    });

    // Also handle h6 elements with content threshold logic
    this.handleH6ElementPageBreaks(element);

    // Add specific CSS for section-header-row protection
    const sectionRowStyle = document.createElement('style');
    sectionRowStyle.textContent = `
      tr.section-header-row {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
        orphans: 3 !important;
        widows: 3 !important;
      }
      
      .page-break-spacer {
        height: 600px !important;
        width: 100% !important;
        background-color: transparent !important;
        border: none !important;
        margin: 0 !important;
        padding: 0 !important;
        page-break-inside: avoid !important;
        break-inside: avoid !important;
        display: block !important;
      }
      
      tr.section-header-row.force-page-break-before {
        margin-top: 20px !important;
      }
      
      div.h6.force-page-break-before {
        margin-top: 20px !important;
      }
    `;
    document.head.appendChild(sectionRowStyle);

    console.log('Section header row and h6 page break fixes applied');
  }

  /**
   * JavaScript utility function to detect and fix section headers at page breaks
   * This specifically targets tr elements with 'section-header-row' class using visual spacing
   */
  public static detectAndFixSectionHeaders(): void {
    // Specifically target tr elements with 'section-header-row' class
    const sectionHeaderRows = document.querySelectorAll('tr.section-header-row') as NodeListOf<HTMLElement>;
    
    console.log(`Found ${sectionHeaderRows.length} section header rows to analyze`);
    
    sectionHeaderRows.forEach((headerRow, index) => {
      const table = headerRow.closest('table, .p-datatable');
      if (table) {
        const rows = table.querySelectorAll('tr') as NodeListOf<HTMLElement>;
        const rowIndex = Array.from(rows).indexOf(headerRow);
        const isLastRow = rowIndex === rows.length - 1;
        const isNearEnd = rowIndex >= rows.length - 3;
        
        // Calculate if row would appear at page end
        const container = headerRow.closest('.pdf-page-break-protected') || document.body;
        const containerRect = container.getBoundingClientRect();
        const rowRect = headerRow.getBoundingClientRect();
        
        // Estimate footer height (assuming 20mm footer)
        const footerHeightPx = 20 * 3.779527559; // Convert mm to px
        const availableSpace = containerRect.height - footerHeightPx - 40; // 40px margin buffer
        const wouldAppearAtPageEnd = rowRect.bottom > (availableSpace - 100); // 100px threshold
        
        if (isLastRow || isNearEnd || wouldAppearAtPageEnd) {
          // Add visual spacing instead of CSS page breaks
          PdfService.addPageBreakSpacing(headerRow, 'before');
          headerRow.classList.add('force-page-break-before');
          
          let reason = 'unknown';
          if (isLastRow) reason = 'last-row';
          else if (isNearEnd) reason = 'near-end';
          else if (wouldAppearAtPageEnd) reason = 'page-end-detected';
          
          headerRow.setAttribute('data-page-break-reason', reason);
          console.log(`Section header row "${headerRow.textContent?.trim()}" will have page break spacing (reason: ${reason})`);
        }
      }
    });
  }

  /**
   * Static method to add page break spacing for canvas-based PDF generation
   */
  public static addPageBreakSpacing(element: HTMLElement, position: 'before' | 'after'): void {
    // Create a page break spacer element
    const spacer = document.createElement('div');
    spacer.className = 'page-break-spacer';
    spacer.style.height = '600px'; // Approximate page height minus margins
    spacer.style.width = '100%';
    spacer.style.backgroundColor = 'transparent';
    spacer.style.border = 'none';
    spacer.style.margin = '0';
    spacer.style.padding = '0';
    spacer.style.pageBreakInside = 'avoid';
    spacer.style.breakInside = 'avoid';
    spacer.style.display = 'block';
    
    // Add data attribute for identification
    spacer.setAttribute('data-page-break-spacer', 'true');
    spacer.setAttribute('data-target-element', 'section-header-row');
    
    if (position === 'before') {
      // Insert spacer before the element
      element.parentNode?.insertBefore(spacer, element);
    } else {
      // Insert spacer after the element
      element.parentNode?.insertBefore(spacer, element.nextSibling);
    }
  }

  /**
   * Test method to verify section header row and h6 element detection and spacing
   * This can be called from browser console for testing
   */
  public static testSectionHeaderRowDetection(): void {
    console.log('=== Testing Section Header Row and H6 Detection ===');
    
    // Test section header rows
    const sectionHeaderRows = document.querySelectorAll('tr.section-header-row') as NodeListOf<HTMLElement>;
    console.log(`Found ${sectionHeaderRows.length} section header rows`);
    
    sectionHeaderRows.forEach((headerRow, index) => {
      const table = headerRow.closest('table, .p-datatable');
      const rowText = headerRow.textContent?.trim();
      
      console.log(`\n--- Section Header ${index + 1}: "${rowText}" ---`);
      
      if (table) {
        const rows = table.querySelectorAll('tr') as NodeListOf<HTMLElement>;
        const rowIndex = Array.from(rows).indexOf(headerRow);
        const isLastRow = rowIndex === rows.length - 1;
        const isNearEnd = rowIndex >= rows.length - 3;
        
        console.log(`Row position: ${rowIndex + 1} of ${rows.length}`);
        console.log(`Is last row: ${isLastRow}`);
        console.log(`Is near end: ${isNearEnd}`);
        
        // Calculate page end detection
        const container = headerRow.closest('.pdf-page-break-protected') || document.body;
        const containerRect = container.getBoundingClientRect();
        const rowRect = headerRow.getBoundingClientRect();
        
        const footerHeightPx = 20 * 3.779527559;
        const availableSpace = containerRect.height - footerHeightPx - 40;
        const wouldAppearAtPageEnd = rowRect.bottom > (availableSpace - 100);
        
        console.log(`Container height: ${containerRect.height}px`);
        console.log(`Row bottom position: ${rowRect.bottom}px`);
        console.log(`Available space: ${availableSpace}px`);
        console.log(`Would appear at page end: ${wouldAppearAtPageEnd}`);
        
        if (isLastRow || isNearEnd || wouldAppearAtPageEnd) {
          console.log(`✅ This section header should have page break spacing`);
          
          // Add visual indicator for testing
          headerRow.style.border = '2px solid red';
          headerRow.style.backgroundColor = '#ffebee';
        } else {
          console.log(`❌ This section header does not need page break spacing`);
        }
      }
    });

    // Test h6 elements
    const h6Elements = document.querySelectorAll('div.h6') as NodeListOf<HTMLElement>;
    console.log(`\nFound ${h6Elements.length} h6 elements`);
    
    h6Elements.forEach((h6Element, index) => {
      const h6Text = h6Element.textContent?.trim();
      
      console.log(`\n--- H6 Element ${index + 1}: "${h6Text}" ---`);
      
      // Check content after h6
      const nextSiblings = PdfService.getNextSiblingsStatic(h6Element);
      let contentHeight = 0;
      let elementCount = 0;
      
      nextSiblings.forEach(sibling => {
        if (sibling.nodeType === Node.ELEMENT_NODE) {
          const rect = (sibling as HTMLElement).getBoundingClientRect();
          contentHeight += rect.height;
          elementCount++;
          
          const nestedElements = (sibling as HTMLElement).querySelectorAll('table, div, tr, td, p, span');
          elementCount += nestedElements.length;
        }
      });
      
      const hasTables = Array.from(nextSiblings).some(sibling => 
        sibling.nodeType === Node.ELEMENT_NODE && 
        (sibling as HTMLElement).querySelector('table, .p-datatable')
      );
      
      const hasSubstantialContent = contentHeight > 200 || elementCount > 5 || hasTables;
      
      console.log(`Content height after: ${contentHeight}px`);
      console.log(`Element count after: ${elementCount}`);
      console.log(`Has tables after: ${hasTables}`);
      console.log(`Has substantial content: ${hasSubstantialContent}`);
      
      // Check if near page end
      const container = h6Element.closest('.pdf-page-break-protected') || document.body;
      const containerRect = container.getBoundingClientRect();
      const elementRect = h6Element.getBoundingClientRect();
      
      const footerHeightPx = 20 * 3.779527559;
      const availableSpace = containerRect.height - footerHeightPx - 40;
      const isNearPageEnd = elementRect.bottom > (availableSpace - 150);
      
      console.log(`Is near page end: ${isNearPageEnd}`);
      
      if (!hasSubstantialContent && isNearPageEnd) {
        console.log(`✅ This h6 should have page break spacing (minimal content after)`);
        
        // Add visual indicator for testing
        h6Element.style.border = '2px solid blue';
        h6Element.style.backgroundColor = '#e3f2fd';
      } else if (hasSubstantialContent) {
        console.log(`❌ This h6 will stay on current page (substantial content after)`);
        
        // Add visual indicator for testing
        h6Element.style.border = '2px solid green';
        h6Element.style.backgroundColor = '#e8f5e8';
      } else {
        console.log(`❌ This h6 does not need page break spacing`);
      }
    });
    
    console.log('\n=== Test Complete ===');
    console.log('Red bordered rows = section headers that need page breaks');
    console.log('Blue bordered h6 = h6 elements that need page breaks (minimal content after)');
    console.log('Green bordered h6 = h6 elements that stay on current page (substantial content after)');
  }

  /**
   * Static helper method to get next siblings
   */
  public static getNextSiblingsStatic(element: HTMLElement): Node[] {
    const siblings: Node[] = [];
    let nextSibling = element.nextSibling;
    
    while (nextSibling) {
      siblings.push(nextSibling);
      nextSibling = nextSibling.nextSibling;
    }
    
    return siblings;
  }

  /**
   * Create canvas for specific page section
   */
  private createPageCanvas(canvas: HTMLCanvasElement, sourceY: number, sourceHeight: number): HTMLCanvasElement {
    const pageCanvas = document.createElement('canvas');
    pageCanvas.width = canvas.width;
    pageCanvas.height = Math.ceil(sourceHeight);

    const pageCtx = pageCanvas.getContext('2d');
    if (pageCtx) {
      // Fill with white background
      pageCtx.fillStyle = '#ffffff';
      pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);

      // Draw the specific portion of the original canvas
      pageCtx.drawImage(
        canvas,
        0, sourceY, canvas.width, sourceHeight,
        0, 0, canvas.width, pageCanvas.height
      );
    }

    return pageCanvas;
  }

  /**
   * Create canvas for specific page section with enhanced content protection
   */
  private createPageCanvasProtected(canvas: HTMLCanvasElement, sourceY: number, sourceHeight: number): HTMLCanvasElement {
    // Validate input parameters to prevent content cutting
    const validatedSourceY = Math.max(0, Math.min(sourceY, canvas.height - 1));
    const validatedSourceHeight = Math.max(1, Math.min(sourceHeight, canvas.height - validatedSourceY));

    const pageCanvas = document.createElement('canvas');
    pageCanvas.width = canvas.width;
    pageCanvas.height = Math.ceil(validatedSourceHeight);

    const pageCtx = pageCanvas.getContext('2d');
    if (pageCtx) {
      // Fill with white background
      pageCtx.fillStyle = '#ffffff';
      pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);

      try {
        // Draw the specific portion of the original canvas with bounds checking
        pageCtx.drawImage(
          canvas,
          0, validatedSourceY, canvas.width, validatedSourceHeight,
          0, 0, canvas.width, pageCanvas.height
        );

        //  console.log(`Canvas section created: Source(${validatedSourceY}, ${validatedSourceHeight}) -> Canvas(${pageCanvas.width}x${pageCanvas.height})`);
      } catch (error) {
        console.error('Error drawing canvas section:', error);
        //  console.log(`Failed params: sourceY=${validatedSourceY}, sourceHeight=${validatedSourceHeight}, canvas=${canvas.width}x${canvas.height}`);

        // Fallback: just fill with white background
        pageCtx.fillStyle = '#ffffff';
        pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
      }
    }

    return pageCanvas;
  }

  /**
   * Store element styles for restoration
   */
  private storeElementStyles(element: HTMLElement): Map<HTMLElement, string> {
    const stylesMap = new Map<HTMLElement, string>();

    // Store main element styles including padding
    const originalStyles = {
      cssText: element.style.cssText,
      paddingBottom: element.style.paddingBottom
    };
    stylesMap.set(element, JSON.stringify(originalStyles));

    // Store nested elements styles
    const textElements = element.querySelectorAll('*') as NodeListOf<HTMLElement>;
    textElements.forEach((el: HTMLElement) => {
      stylesMap.set(el, el.style.cssText);
    });

    return stylesMap;
  }

  /**
   * Calculate true content dimensions to prevent content cutting
   */
  private calculateTrueContentDimensions(element: HTMLElement): { width: number; height: number } {
    // Store original styles
    const originalOverflow = element.style.overflow;
    const originalHeight = element.style.height;
    const originalMaxHeight = element.style.maxHeight;
    const originalMinHeight = element.style.minHeight;
    const originalOverflowY = element.style.overflowY;
    const originalOverflowX = element.style.overflowX;

    // Temporarily expand element to show all content
    element.style.overflow = 'visible';
    element.style.overflowX = 'visible';
    element.style.overflowY = 'visible';
    element.style.height = 'auto';
    element.style.maxHeight = 'none';
    element.style.minHeight = 'auto';

    // Also expand all child elements that might be constraining content
    const allElements = element.querySelectorAll('*') as NodeListOf<HTMLElement>;
    const originalChildStyles: Array<{ element: HTMLElement; styles: any }> = [];

    allElements.forEach(el => {
      const originalElStyles = {
        overflow: el.style.overflow,
        overflowX: el.style.overflowX,
        overflowY: el.style.overflowY,
        height: el.style.height,
        maxHeight: el.style.maxHeight,
        minHeight: el.style.minHeight
      };
      originalChildStyles.push({ element: el, styles: originalElStyles });

      // Expand child element
      el.style.overflow = 'visible';
      el.style.overflowX = 'visible';
      el.style.overflowY = 'visible';
      if (el.style.height && el.style.height !== 'auto') {
        el.style.height = 'auto';
      }
      if (el.style.maxHeight && el.style.maxHeight !== 'none') {
        el.style.maxHeight = 'none';
      }
    });

    // Force multiple reflows to ensure accurate measurements
    element.offsetHeight;
    requestAnimationFrame(() => element.offsetHeight);
    //element.scrollIntoView();

    // Get multiple dimension measurements
    const rect = element.getBoundingClientRect();
    const computedStyle = window.getComputedStyle(element);

    // Calculate padding and border values
    const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
    const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
    const borderLeft = parseFloat(computedStyle.borderLeftWidth) || 0;
    const borderRight = parseFloat(computedStyle.borderRightWidth) || 0;
    const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
    const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;
    const borderTop = parseFloat(computedStyle.borderTopWidth) || 0;
    const borderBottom = parseFloat(computedStyle.borderBottomWidth) || 0;

    // Get various dimension measurements
    const scrollWidth = element.scrollWidth;
    const scrollHeight = element.scrollHeight;
    const offsetWidth = element.offsetWidth;
    const offsetHeight = element.offsetHeight;
    const clientWidth = element.clientWidth;
    const clientHeight = element.clientHeight;

    // Calculate content dimensions by measuring child elements
    const childContentDimensions = this.measureChildContentDimensions(element);

    // Use the maximum of all measurements to ensure we don't cut content
    const trueWidth = Math.max(
      rect.width,
      scrollWidth,
      offsetWidth,
      clientWidth,
      childContentDimensions.width,
      paddingLeft + paddingRight + borderLeft + borderRight + this.getContentWidth(element)
    );

    const trueHeight = Math.max(
      rect.height,
      scrollHeight,
      offsetHeight,
      clientHeight,
      childContentDimensions.height,
      paddingTop + paddingBottom + borderTop + borderBottom + this.getContentHeight(element)
    );

    ////  console.log(`Content Dimension Measurements:
    //   - BoundingRect: ${rect.width.toFixed(1)} x ${rect.height.toFixed(1)}
    //   - Scroll: ${scrollWidth} x ${scrollHeight}
    //   - Offset: ${offsetWidth} x ${offsetHeight}
    //   - Client: ${clientWidth} x ${clientHeight}
    //   - Child Content: ${childContentDimensions.width.toFixed(1)} x ${childContentDimensions.height.toFixed(1)}
    //   - Final: ${trueWidth.toFixed(1)} x ${trueHeight.toFixed(1)}`);

    // Restore original styles for main element
    element.style.overflow = originalOverflow;
    element.style.overflowX = originalOverflowX;
    element.style.overflowY = originalOverflowY;
    element.style.height = originalHeight;
    element.style.maxHeight = originalMaxHeight;
    element.style.minHeight = originalMinHeight;

    // Restore original styles for child elements
    originalChildStyles.forEach(({ element: el, styles }) => {
      el.style.overflow = styles.overflow;
      el.style.overflowX = styles.overflowX;
      el.style.overflowY = styles.overflowY;
      el.style.height = styles.height;
      el.style.maxHeight = styles.maxHeight;
      el.style.minHeight = styles.minHeight;
    });

    return {
      width: Math.ceil(trueWidth),
      height: Math.ceil(trueHeight)
    };
  }

  /**
   * Measure child content dimensions by examining all child elements
   */
  private measureChildContentDimensions(element: HTMLElement): { width: number; height: number } {
    let maxWidth = 0;
    let totalHeight = 0;
    let maxChildBottom = 0;
    let maxChildRight = 0;

    const children = Array.from(element.children) as HTMLElement[];
    const elementRect = element.getBoundingClientRect();

    children.forEach(child => {
      const childRect = child.getBoundingClientRect();
      const childComputedStyle = window.getComputedStyle(child);

      // Calculate child dimensions including margins
      const marginLeft = parseFloat(childComputedStyle.marginLeft) || 0;
      const marginRight = parseFloat(childComputedStyle.marginRight) || 0;
      const marginTop = parseFloat(childComputedStyle.marginTop) || 0;
      const marginBottom = parseFloat(childComputedStyle.marginBottom) || 0;

      const childTotalWidth = childRect.width + marginLeft + marginRight;
      const childTotalHeight = childRect.height + marginTop + marginBottom;

      // Calculate position relative to parent
      const childBottom = (childRect.bottom - elementRect.top) + marginBottom;
      const childRight = (childRect.right - elementRect.left) + marginRight;

      maxWidth = Math.max(maxWidth, childTotalWidth);
      totalHeight += childTotalHeight;
      maxChildBottom = Math.max(maxChildBottom, childBottom);
      maxChildRight = Math.max(maxChildRight, childRight);

      // For nested tables, measure their content too
      if (child.tagName === 'TABLE' || child.classList.contains('p-datatable')) {
        const nestedDimensions = this.measureTableContent(child);
        maxWidth = Math.max(maxWidth, nestedDimensions.width);
        maxChildBottom = Math.max(maxChildBottom, childBottom + nestedDimensions.height);
      }
    });

    return {
      width: Math.max(maxWidth, maxChildRight),
      height: Math.max(totalHeight, maxChildBottom)
    };
  }

  /**
   * Measure table content including all rows and nested content
   */
  private measureTableContent(table: HTMLElement): { width: number; height: number } {
    let totalHeight = 0;
    let maxWidth = 0;

    // Measure all table rows
    const rows = table.querySelectorAll('tr') as NodeListOf<HTMLElement>;
    rows.forEach(row => {
      const rowRect = row.getBoundingClientRect();
      totalHeight += rowRect.height;
      maxWidth = Math.max(maxWidth, rowRect.width);

      // Check for nested content in cells
      const cells = row.querySelectorAll('td, th') as NodeListOf<HTMLElement>;
      cells.forEach(cell => {
        const cellRect = cell.getBoundingClientRect();
        const nestedTables = cell.querySelectorAll('table') as NodeListOf<HTMLElement>;

        nestedTables.forEach(nestedTable => {
          const nestedRect = nestedTable.getBoundingClientRect();
          totalHeight = Math.max(totalHeight, nestedRect.height);
          maxWidth = Math.max(maxWidth, nestedRect.width);
        });
      });
    });

    return { width: maxWidth, height: totalHeight };
  }

  /**
   * Get the actual content width by measuring child elements
   */
  private getContentWidth(element: HTMLElement): number {
    let maxWidth = 0;
    const children = element.children;

    for (let i = 0; i < children.length; i++) {
      const child = children[i] as HTMLElement;
      const childRect = child.getBoundingClientRect();
      const childRightEdge = childRect.width + (parseFloat(window.getComputedStyle(child).marginLeft) || 0) + (parseFloat(window.getComputedStyle(child).marginRight) || 0);
      maxWidth = Math.max(maxWidth, childRightEdge);
    }

    return maxWidth;
  }

  /**
   * Get the actual content height by measuring all child elements
   */
  private getContentHeight(element: HTMLElement): number {
    let totalHeight = 0;
    const children = element.children;

    for (let i = 0; i < children.length; i++) {
      const child = children[i] as HTMLElement;
      const childRect = child.getBoundingClientRect();
      const childHeight = childRect.height + (parseFloat(window.getComputedStyle(child).marginTop) || 0) + (parseFloat(window.getComputedStyle(child).marginBottom) || 0);
      totalHeight += childHeight;
    }

    return totalHeight;
  }

  /**
   * Expand element to show all content for full capture
   */
  private expandElementForFullCapture(element: HTMLElement): void {
    // Remove any height restrictions
    element.style.height = 'auto';
    element.style.maxHeight = 'none';
    element.style.minHeight = 'auto';
    element.style.overflow = 'visible';
    element.style.overflowX = 'visible';
    element.style.overflowY = 'visible';

    // Expand all child elements that might be collapsed or hidden
    const allElements = element.querySelectorAll('*') as NodeListOf<HTMLElement>;
    allElements.forEach(el => {
      // Remove height restrictions
      if (el.style.height && el.style.height !== 'auto') {
        el.style.height = 'auto';
      }
      if (el.style.maxHeight && el.style.maxHeight !== 'none') {
        el.style.maxHeight = 'none';
      }

      // Make sure overflow is visible
      if (el.style.overflow === 'hidden' || el.style.overflow === 'scroll') {
        el.style.overflow = 'visible';
      }

      // Expand collapsed panels and accordions
      if (el.classList.contains('p-panel-content') ||
        el.classList.contains('p-accordion-content') ||
        el.classList.contains('collapse') ||
        el.classList.contains('collapsed')) {
        el.style.display = 'block';
        el.style.visibility = 'visible';
        el.style.height = 'auto';
        el.style.maxHeight = 'none';
        el.style.overflow = 'visible';
      }

      // Ensure tables show all content
      if (el.tagName === 'TABLE') {
        el.style.height = 'auto';
        el.style.tableLayout = 'auto';
        el.style.overflow = 'visible';
      }

      // Ensure table cells show all content
      if (el.tagName === 'TD' || el.tagName === 'TH') {
        el.style.height = 'auto';
        el.style.whiteSpace = 'normal';
        el.style.overflow = 'visible';
        el.style.textOverflow = 'clip';
      }

      // Special handling for datatable wrappers
      if (el.classList.contains('p-datatable-wrapper')) {
        el.style.overflow = 'visible';
        el.style.maxHeight = 'none';
        el.style.height = 'auto';
      }
    });

    // Force reflow
    element.offsetHeight;
  }

  /**
   * Prepare element for PDF capture based on content type
   */
  private async prepareElementForPDF(element: HTMLElement, contentType: string): Promise<void> {
    // First, expand element to show all content
    this.expandElementForFullCapture(element);

    // Apply base PDF-optimized styles
    element.style.position = 'relative';
    element.style.backgroundColor = '#ffffff';
    element.style.color = '#000000';
    element.style.visibility = 'visible';
    element.style.display = 'block';
    element.style.opacity = '1';
    element.style.transform = 'none';
    element.style.boxSizing = 'border-box';
    element.style.wordWrap = 'break-word';
    element.style.overflowWrap = 'break-word';

    // Reserve space for footer by adding bottom padding
    const currentPadding = parseFloat(window.getComputedStyle(element).paddingBottom) || 0;
    const footerPadding = (this.getFooterHeight(contentType) * 3.779527559); // Convert mm to px (1mm = 3.779527559px)
    element.style.paddingBottom = `${currentPadding + footerPadding}px`;

    // Apply PrimeNG component protection to prevent splitting
    this.protectPrimeNGComponents(element);

    // Apply section header page break fixes
    this.applySectionHeaderPageBreakFixes(element);

    // Apply content-specific optimizations
    switch (contentType) {
      case 'comparison':
        await this.optimizeForComparison(element);
        break;
      case 'profile':
        await this.optimizeForProfile(element);
        break;
      default:
        await this.optimizeGeneral(element);
        break;
    }

    // Ensure all images are loaded
    await this.ensureImagesLoaded(element);

    // Remove borders from icons
    this.cleanupIcons(element);

    // Force reflow and stabilize layout
    element.offsetHeight;
    await this.waitForNextFrame();
  }

  /**
   * Optimize element for comparison content (tables, side-by-side layouts)
   */
  private async optimizeForComparison(element: HTMLElement): Promise<void> {
    // Check if this is the indexed interest comparison page
    const isIndexedInterest = element.classList.contains('indexed-interest-pdf-page') ||
      element.querySelector('.indexed-interest-pdf-page') ||
      element.querySelector('.basic-interests-table');

    if (isIndexedInterest) {
      await this.optimizeIndexedInterestComparison(element);
    } else {
      await this.optimizeGeneralComparison(element);
    }
  }

  /**
   * Optimize specifically for indexed interest comparison with nested tables
   */
  private async optimizeIndexedInterestComparison(element: HTMLElement): Promise<void> {
    //  console.log('Optimizing indexed interest comparison for PDF...');

    // Check for cancellation at the start of optimization
    if (this.isCancelled) {
      //  console.log('PDF generation cancelled during indexed interest optimization');
      return;
    }

    // Remove scrollable constraints from all datatable wrappers
    const datatableWrappers = element.querySelectorAll('.p-datatable-wrapper') as NodeListOf<HTMLElement>;
    datatableWrappers.forEach(wrapper => {
      wrapper.style.maxHeight = 'none';
      wrapper.style.height = 'auto';
      wrapper.style.overflow = 'visible';
      wrapper.style.overflowY = 'visible';
      wrapper.style.overflowX = 'visible';
    });

    // Optimize main interest tables
    const interestTables = element.querySelectorAll('.basic-interests-table') as NodeListOf<HTMLElement>;
    interestTables.forEach(table => {
      table.style.width = '100%';
      table.style.tableLayout = 'fixed';
      table.style.borderCollapse = 'collapse';

      // Remove frozen columns for PDF
      const frozenCells = table.querySelectorAll('[frozen="true"], .p-frozen-column') as NodeListOf<HTMLElement>;
      frozenCells.forEach(cell => {
        cell.style.position = 'static';
        cell.style.left = 'auto';
        cell.style.zIndex = 'auto';
        cell.style.backgroundColor = 'transparent';
      });
    });

    // Optimize inner nested tables
    const innerTables = element.querySelectorAll('.inner-table table, .pdf-inner-table') as NodeListOf<HTMLElement>;
    innerTables.forEach((table: HTMLElement) => {
      table.style.width = '100%';
      table.style.tableLayout = 'auto'; // Allow content to determine width
      table.style.borderCollapse = 'collapse';
      table.style.fontSize = '7px';
      table.style.lineHeight = '1.1';

      // Optimize inner table headers
      const innerHeaders = table.querySelectorAll('th') as NodeListOf<HTMLElement>;
      innerHeaders.forEach((header: HTMLElement) => {
        header.style.padding = '2px 3px';
        header.style.fontSize = '7px';
        header.style.fontWeight = 'bold';
        header.style.backgroundColor = '#f8f9fa';
        header.style.border = '1px solid #dee2e6';
        header.style.textAlign = 'center';
        header.style.verticalAlign = 'middle';
        header.style.whiteSpace = 'nowrap';
      });

      // Optimize inner table cells
      const innerCells = table.querySelectorAll('td') as NodeListOf<HTMLElement>;
      innerCells.forEach((cell: HTMLElement) => {
        cell.style.padding = '1px 2px';
        cell.style.fontSize = '7px';
        cell.style.border = '1px solid #dee2e6';
        cell.style.verticalAlign = 'top';
        cell.style.wordWrap = 'break-word';
        cell.style.overflowWrap = 'break-word';
        cell.style.lineHeight = '1.1';
      });
    });

    // Optimize marketing name rows and index rows
    const marketingRows = element.querySelectorAll('.marketing-name-row') as NodeListOf<HTMLElement>;
    marketingRows.forEach(row => {
      row.style.pageBreakInside = 'avoid';
    });

    // Optimize index used divs
    const indexDivs = element.querySelectorAll('.div-indexused') as NodeListOf<HTMLElement>;
    indexDivs.forEach(div => {
      div.style.display = 'flex';
      div.style.alignItems = 'center';
      div.style.padding = '1px 2px';
      div.style.borderTop = '1px solid #dee2e6';
      div.style.fontSize = '7px';
      div.style.minHeight = '18px';
    });

    // Optimize radio button replacements for PDF
    const pdfSelectionIndicators = element.querySelectorAll('.pdf-selection-indicator') as NodeListOf<HTMLElement>;
    pdfSelectionIndicators.forEach(indicator => {
      indicator.style.fontSize = '8px';
      indicator.style.fontWeight = 'bold';
      indicator.style.color = indicator.classList.contains('selected') ? '#28a745' : '#6c757d';
      indicator.style.marginRight = '3px';
    });

    // Optimize descriptions
    const descriptions = element.querySelectorAll('.interest-description, .pdf-description') as NodeListOf<HTMLElement>;
    descriptions.forEach(desc => {
      desc.style.padding = '2px 4px';
      desc.style.fontSize = '7px';
      desc.style.lineHeight = '1.2';
      desc.style.backgroundColor = '#f8f9fa';
      desc.style.border = '1px solid #dee2e6';
      desc.style.wordBreak = 'break-word';
      desc.style.pageBreakInside = 'avoid';
    });

    // Force layout recalculation
    element.offsetHeight;
    await this.waitForNextFrame();
  }

  /**
   * General comparison optimization for non-indexed interest tables
   */
  private async optimizeGeneralComparison(element: HTMLElement): Promise<void> {
    const tables = element.querySelectorAll('table') as NodeListOf<HTMLElement>;
    tables.forEach((table: HTMLElement) => {
      table.style.pageBreakInside = 'auto';
      table.style.borderCollapse = 'collapse';
      table.style.fontSize = '8px';
      table.style.width = '100%';

      // Optimize header cells for comparison
      const headerCells = table.querySelectorAll('th') as NodeListOf<HTMLElement>;
      headerCells.forEach((cell: HTMLElement) => {
        cell.style.padding = '4px 6px';
        cell.style.fontSize = '9px';
        cell.style.fontWeight = 'bold';
        cell.style.lineHeight = '1.1';
        cell.style.wordBreak = 'break-word';
        cell.style.backgroundColor = '#f8f9fa';
        cell.style.textAlign = 'center';
      });

      // Optimize data cells for comparison
      const dataCells = table.querySelectorAll('td') as NodeListOf<HTMLElement>;
      dataCells.forEach((cell: HTMLElement) => {
        cell.style.padding = '3px 5px';
        cell.style.fontSize = '8px';
        cell.style.fontWeight = 'normal';
        cell.style.lineHeight = '1.1';
        cell.style.wordBreak = 'break-word';
        cell.style.textAlign = 'center';
      });
    });
  }

  /**
   * Optimize element for profile content (detailed product information)
   */
  private async optimizeForProfile(element: HTMLElement): Promise<void> {
    // Standardize fonts for profile content
    await this.standardizeFonts(element);

    // Optimize tables for profile details
    this.optimizeProfileTables(element);

    // Optimize panels and accordions
    this.optimizeProfilePanels(element);
    this.optimizeProfileAccordions(element);
  }

  /**
   * General optimization for all content types
   */
  private async optimizeGeneral(element: HTMLElement): Promise<void> {
    const textElements = element.querySelectorAll('*') as NodeListOf<HTMLElement>;
    textElements.forEach((el: HTMLElement) => {
      // Ensure readable font sizes
      const currentSize = parseFloat(window.getComputedStyle(el).fontSize);
      if (currentSize < 10) {
        el.style.fontSize = '10px';
      }
      el.style.lineHeight = '1.3';
      el.style.textRendering = 'optimizeLegibility';
      (el.style as any).webkitFontSmoothing = 'antialiased';
    });

    // Basic table optimization
    const tables = element.querySelectorAll('table') as NodeListOf<HTMLElement>;
    tables.forEach((table: HTMLElement) => {
      table.style.pageBreakInside = 'auto';
      table.style.borderCollapse = 'collapse';
    });
  }

  /**
   * Standardize fonts for profile content
   */
  private async standardizeFonts(element: HTMLElement): Promise<void> {
    const textElements = element.querySelectorAll('*') as NodeListOf<HTMLElement>;
    const elementsArray = Array.from(textElements);

    // Process elements in batches to prevent blocking
    const batchSize = 50;
    for (let i = 0; i < elementsArray.length; i += batchSize) {
      // Check for cancellation before processing each batch
      if (this.isCancelled) {
        //  console.log('PDF generation cancelled during font standardization');
        return;
      }

      const batch = elementsArray.slice(i, i + batchSize);

      batch.forEach((el: HTMLElement) => {
        // Standardize font sizes based on element type
        if (el.tagName === 'H1') {
          el.style.fontSize = '16px';
          el.style.fontWeight = 'bold';
        } else if (el.tagName === 'H2') {
          el.style.fontSize = '14px';
          el.style.fontWeight = 'bold';
        } else if (el.tagName === 'H3') {
          el.style.fontSize = '12px';
          el.style.fontWeight = 'bold';
        } else if (el.tagName === 'TH') {
          el.style.fontSize = '10px';
          el.style.fontWeight = 'bold';
        } else if (el.tagName === 'TD') {
          el.style.fontSize = '9px';
          el.style.fontWeight = 'normal';
        } else {
          const currentSize = parseFloat(window.getComputedStyle(el).fontSize);
          if (currentSize < 9) el.style.fontSize = '9px';
          else if (currentSize > 14) el.style.fontSize = '12px';
        }

        el.style.lineHeight = '1.3';
        el.style.textRendering = 'optimizeLegibility';
        el.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      });

      // Yield control after each batch
      if (i + batchSize < elementsArray.length) {
        await this.waitForNextFrame();
      }
    }
  }

  /**
   * Optimize tables for profile content
   */
  private optimizeProfileTables(element: HTMLElement): void {
    const tables = element.querySelectorAll('table') as NodeListOf<HTMLElement>;
    tables.forEach((table: HTMLElement) => {
      table.style.pageBreakInside = 'auto';
      table.style.borderCollapse = 'collapse';
      table.style.fontSize = '9px';
      table.style.width = '100%';

      const headerCells = table.querySelectorAll('th') as NodeListOf<HTMLElement>;
      headerCells.forEach((cell: HTMLElement) => {
        cell.style.padding = '6px 8px';
        cell.style.fontSize = '10px';
        cell.style.fontWeight = 'bold';
        cell.style.backgroundColor = '#f8f9fa';
      });

      const dataCells = table.querySelectorAll('td') as NodeListOf<HTMLElement>;
      dataCells.forEach((cell: HTMLElement) => {
        cell.style.padding = '4px 6px';
        cell.style.fontSize = '9px';
        cell.style.fontWeight = 'normal';
      });
    });
  }

  /**
   * Optimize panels for profile content
   */
  private optimizeProfilePanels(element: HTMLElement): void {
    const panels = element.querySelectorAll('.p-panel') as NodeListOf<HTMLElement>;
    panels.forEach((panel: HTMLElement) => {
      const header = panel.querySelector('.p-panel-header') as HTMLElement;
      const content = panel.querySelector('.p-panel-content') as HTMLElement;

      if (header) {
        header.style.padding = '10px 12px';
        header.style.fontSize = '11px';
        header.style.fontWeight = 'bold';
        header.style.backgroundColor = '#f8f9fa';
        header.style.border = '1px solid #dee2e6';

        // Remove panel toggle icons
        const panelIcons = header.querySelectorAll('.p-panel-header-icon, .p-panel-toggle-icon') as NodeListOf<HTMLElement>;
        panelIcons.forEach(icon => icon.remove());
      }

      if (content) {
        content.style.padding = '10px 12px';
        content.style.border = '1px solid #dee2e6';
        content.style.borderTop = 'none';
        content.style.backgroundColor = '#ffffff';
      }

      panel.style.marginBottom = '15px';
      panel.style.pageBreakInside = 'avoid';
    });
  }

  /**
   * Optimize accordions for profile content
   */
  private optimizeProfileAccordions(element: HTMLElement): void {
    const accordions = element.querySelectorAll('.p-accordion') as NodeListOf<HTMLElement>;
    accordions.forEach((accordion: HTMLElement) => {
      const accordionTabs = accordion.querySelectorAll('.p-accordion-tab') as NodeListOf<HTMLElement>;
      accordionTabs.forEach((tab: HTMLElement) => {
        const headerLink = tab.querySelector('.p-accordion-header-link') as HTMLElement;
        const content = tab.querySelector('.p-accordion-content') as HTMLElement;

        if (headerLink) {
          headerLink.style.padding = '10px 14px';
          headerLink.style.fontSize = '11px';
          headerLink.style.fontWeight = 'bold';
          headerLink.style.backgroundColor = '#f8f9fa';
          headerLink.style.border = '1px solid #dee2e6';

          // Remove accordion toggle icons
          const toggleIcons = headerLink.querySelectorAll('.p-accordion-toggle-icon') as NodeListOf<HTMLElement>;
          toggleIcons.forEach(icon => icon.remove());
        }

        if (content) {
          content.style.padding = '10px 14px';
          content.style.border = '1px solid #dee2e6';
          content.style.borderTop = 'none';
          content.style.backgroundColor = '#ffffff';
        }

        tab.style.marginBottom = '4px';
        tab.style.pageBreakInside = 'avoid';
      });
    });
  }

  /**
   * Ensure all images are loaded
   */
  private async ensureImagesLoaded(element: HTMLElement): Promise<void> {
    const images = element.querySelectorAll('img') as NodeListOf<HTMLImageElement>;

    if (images.length === 0) return;

    const imagePromises = Array.from(images).map(img => {
      return new Promise<void>((resolve) => {
        const timeout = setTimeout(() => resolve(), 3000);

        if (img.complete && img.naturalWidth > 0) {
          clearTimeout(timeout);
          if (!img.style.width && !img.style.height) {
            img.style.maxWidth = '100%';
            img.style.height = 'auto';
          }
          resolve();
        } else {
          img.onload = () => {
            clearTimeout(timeout);
            if (!img.style.width && !img.style.height) {
              img.style.maxWidth = '100%';
              img.style.height = 'auto';
            }
            resolve();
          };
          img.onerror = () => {
            clearTimeout(timeout);
            resolve();
          };
        }
      });
    });

    await Promise.all(imagePromises);
  }

  /**
   * Clean up icons appearance
   */
  private cleanupIcons(element: HTMLElement): void {
    const allIcons = element.querySelectorAll('i, .pi, .fa, .fas, .far, .fab, span[class*="icon"]') as NodeListOf<HTMLElement>;
    allIcons.forEach(icon => {
      icon.style.border = 'none';
      icon.style.outline = 'none';
      icon.style.boxShadow = 'none';
    });
  }

  /**
   * Restore element styles after PDF capture
   */
  private restoreElementStyles(element: HTMLElement, stylesMap: Map<HTMLElement, string>): void {
    stylesMap.forEach((originalStyle, el) => {
      if (el && el.parentNode) {
        try {
          // Check if this is the main element with stored padding
          if (el === element && originalStyle.startsWith('{')) {
            const parsedStyles = JSON.parse(originalStyle);
            el.style.cssText = parsedStyles.cssText;
            if (parsedStyles.paddingBottom) {
              el.style.paddingBottom = parsedStyles.paddingBottom;
            }
          } else {
            // Regular style restoration for other elements
            el.style.cssText = originalStyle;
          }
        } catch (error) {
          // Fallback to regular style restoration
          el.style.cssText = originalStyle;
        }
      }
    });
  }

  /**
   * Clean up canvas to free memory
   */
  private cleanupCanvas(canvas: HTMLCanvasElement): void {
    canvas.width = 0;
    canvas.height = 0;
  }

  /**
   * Wait for next animation frame - better than setTimeout for performance
   */
  private waitForNextFrame(): Promise<void> {
    return new Promise(resolve => {
      if (typeof requestAnimationFrame !== 'undefined') {
        requestAnimationFrame(() => resolve());
      } else {
        setTimeout(() => resolve(), 16); // Fallback: ~60fps
      }
    });
  }

  /**
   * Utility delay function
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Debug method to log element dimensions and content info
   */
  private logElementDebugInfo(element: HTMLElement, phase: string): void {
    const rect = element.getBoundingClientRect();
    const computedStyle = window.getComputedStyle(element);

    //  console.log(`=== Element Debug Info (${phase}) ===`);
    //  console.log(`BoundingRect: ${rect.width.toFixed(1)} x ${rect.height.toFixed(1)}`);
    //  console.log(`ScrollDimensions: ${element.scrollWidth} x ${element.scrollHeight}`);
    //  console.log(`OffsetDimensions: ${element.offsetWidth} x ${element.offsetHeight}`);
    //  console.log(`ClientDimensions: ${element.clientWidth} x ${element.clientHeight}`);
    //  console.log(`Overflow: ${computedStyle.overflow}, ${computedStyle.overflowX}, ${computedStyle.overflowY}`);
    //  console.log(`Height style: ${element.style.height}, maxHeight: ${element.style.maxHeight}`);
    //  console.log(`Children count: ${element.children.length}`);

    // Log info about child elements that might be causing issues
    const hiddenElements = element.querySelectorAll('[style*="display: none"], [style*="visibility: hidden"]');
    if (hiddenElements.length > 0) {
      //  console.log(`Hidden elements found: ${hiddenElements.length}`);
    }

    const overflowElements = element.querySelectorAll('[style*="overflow: hidden"], [style*="overflow: scroll"]');
    if (overflowElements.length > 0) {
      //  console.log(`Overflow elements found: ${overflowElements.length}`);
    }

    //  console.log(`=== End Debug Info ===`);
  }

  /**
   * Emit PDF generation progress
   */
  private emitProgress(currentPage: number, totalPages: number, stage: string, percentage: number): void {
    this.pdfProgress.emit({
      currentPage,
      totalPages,
      stage,
      percentage: Math.round(percentage)
    });
  }

  /**
   * Show/hide loader
   */
  onShowLoader(show: boolean): void {
    this.showLoader.emit(show);
  }

  /**
   * Check if PDF generation is in progress
   */
  public isGeneratingPDF(): boolean {
    return this.isGenerating;
  }

  /**
   * Generate PDF with mixed orientations (portrait and landscape pages)
   */
  private async generateMixedOrientationPDF(pdfPages: NodeListOf<HTMLElement>, options: PDFOptions): Promise<void> {
    ////  console.log('Generating PDF with mixed orientations...');

    // Separate portrait and landscape pages
    const portraitPages: HTMLElement[] = [];
    const landscapePages: HTMLElement[] = [];

    Array.from(pdfPages).forEach(page => {
      if (page.classList.contains('indexed-interest-pdf-page')) {
        landscapePages.push(page);
      } else {
        portraitPages.push(page);
      }
    });

    // Create a main PDF document with the orientation of the first page
    const firstPageLandscape = pdfPages[0].classList.contains('indexed-interest-pdf-page');
    const mainOrientation = firstPageLandscape ? 'landscape' as const : 'portrait' as const;
    const config = this.getPDFConfig({ ...options, orientation: mainOrientation });
    const pdf = new jsPDF(config.orientation as any, 'mm', 'a4');
    pdf.deletePage(1); // Remove the default blank page

    // Calculate total PDF pages that will be created
    let totalPDFPages = 0;
    for (let i = 0; i < pdfPages.length; i++) {
      const pageElement = pdfPages[i];
      const isLandscape = pageElement.classList.contains('indexed-interest-pdf-page');
      const pageConfig = this.getPDFConfig({
        ...options,
        orientation: isLandscape ? 'landscape' as const : 'portrait' as const
      });

      // Estimate page count based on element dimensions
      const rect = pageElement.getBoundingClientRect();
      if (rect.height > 0) {
        const estimatedHeight = rect.height * (pageConfig.scale || 1.5);
        const estimatedHeightMM = estimatedHeight * 0.264583; // Convert px to mm

        if (estimatedHeightMM <= pageConfig.contentMaxHeight) {
          totalPDFPages += 1;
        } else {
          const effectivePageHeight = pageConfig.contentMaxHeight - (pageConfig.overlapBuffer * 2);
          const subPages = Math.ceil(estimatedHeightMM / effectivePageHeight);
          totalPDFPages += subPages;
        }
      } else {
        totalPDFPages += 1; // Fallback to 1 page
      }
    }

    console.log(`Mixed orientation PDF: Total PDF pages calculated: ${totalPDFPages} (from ${pdfPages.length} source pages)`);

    let pageCounter = 0;
    const totalPages = pdfPages.length;

    // Process pages in their original order
    for (let i = 0; i < pdfPages.length; i++) {
      // Check for cancellation before processing each page
      if (this.isCancelled) {
        //  console.log('PDF generation cancelled by user');
        return;
      }

      const pageElement = pdfPages[i];
      const isLandscape = pageElement.classList.contains('indexed-interest-pdf-page');
      const progressPercentage = 10 + ((i / totalPages) * 80);
      this.emitProgress(i + 1, totalPages, `Processing page ${i + 1}...`, progressPercentage);

      //  console.log(`Processing mixed-orientation page ${i + 1}/${totalPages} (${isLandscape ? 'landscape' : 'portrait'})...`);

      // If orientation differs from main PDF, we need to add a page break and change orientation
      if (isLandscape !== (mainOrientation === 'landscape')) {
        // For mixed orientations, we'll rotate the content instead of changing PDF orientation
        // This is a limitation of jsPDF but provides better compatibility
        //  console.log(`Page ${i + 1}: Adjusting for different orientation`);
      }

      // Use appropriate config for this page
      const pageConfig = this.getPDFConfig({
        ...options,
        orientation: isLandscape ? 'landscape' as const : 'portrait' as const
      });

      // Store original styles for restoration
      const originalStyles = this.storeElementStyles(pageElement);

      try {
        // Debug: Log element info before preparation
        this.logElementDebugInfo(pageElement, `Before Preparation - Mixed Page ${i + 1}`);

        // Prepare element for PDF capture
        await this.prepareElementForPDF(pageElement, options.contentType || 'general');

        // Check for cancellation after preparation
        if (this.isCancelled) {
          this.restoreElementStyles(pageElement, originalStyles);
          //  console.log('PDF generation cancelled during page preparation');
          return;
        }

        // Debug: Log element info after preparation
        this.logElementDebugInfo(pageElement, `After Preparation - Mixed Page ${i + 1}`);

        // Allow time for layout stabilization
        await this.waitForNextFrame();

        // Additional stabilization time for complex content
        await this.delay(300);

        // Check for cancellation before canvas capture
        if (this.isCancelled) {
          this.restoreElementStyles(pageElement, originalStyles);
          //  console.log('PDF generation cancelled before canvas capture');
          return;
        }

        // Capture element with html2canvas using page-specific config
        const canvas = await this.captureElementAsCanvas(pageElement, pageConfig);

        // Check for cancellation after canvas capture
        if (this.isCancelled) {
          this.restoreElementStyles(pageElement, originalStyles);
          if (canvas) {
            this.cleanupCanvas(canvas);
          }
          //  console.log('PDF generation cancelled after canvas capture');
          return;
        }

        if (canvas && canvas.width > 0 && canvas.height > 0) {
          //  console.log(`Canvas captured successfully: ${canvas.width}x${canvas.height} for mixed page ${i + 1}`);

          // Add pages to PDF - use main config for consistent PDF structure
          await this.addCanvasToPDF(pdf, canvas, config, i + 1, totalPDFPages);

          // Clean up canvas to free memory
          this.cleanupCanvas(canvas);
        } else {
          console.warn(`Failed to generate canvas for mixed page ${i + 1}`);
          console.warn(`Canvas dimensions: ${canvas?.width || 'undefined'}x${canvas?.height || 'undefined'}`);
        }
      } catch (error) {
        console.error(`Error processing mixed page ${i + 1}:`, error);
      } finally {
        // Restore original element styles
        this.restoreElementStyles(pageElement, originalStyles);
      }

      // Yield control to prevent UI blocking
      await this.waitForNextFrame();

      // Force garbage collection if available
      if ((window as any).gc) {
        (window as any).gc();
      }
    }

    // Final check for cancellation before saving
    if (this.isCancelled) {
      //  console.log('PDF generation cancelled before saving');
      return;
    }

    this.emitProgress(totalPages, totalPages, 'Finalizing mixed-orientation PDF...', 95);

    // Update all footers with the correct total page count
    const finalTotalPages = pdf.getNumberOfPages();
    console.log(`Final mixed-orientation PDF has ${finalTotalPages} pages - updating all footers`);
    this.updateAllFooters(pdf, finalTotalPages, config);

    // Generate filename and trigger download
    const filename = options.filename || `Mixed_PDF_Export_${new Date().getTime()}.pdf`;
    pdf.save(filename);

    this.emitProgress(totalPages, totalPages, 'Mixed-orientation PDF Generated Successfully!', 100);
    //  console.log(`Mixed-orientation PDF generation completed: ${filename}`);
  }

  // Convenience methods for different content types
  async generateProfilePDF(options: Partial<PDFOptions> = {}): Promise<void> {
    await this.generatePDF({ ...options, contentType: 'profile' });
  }

  async generateComparisonPDF(options: Partial<PDFOptions> = {}): Promise<void> {
    await this.generatePDF({ ...options, contentType: 'comparison', orientation: 'landscape' });
  }

  async generateGeneralPDF(options: Partial<PDFOptions> = {}): Promise<void> {
    await this.generatePDF({ ...options, contentType: 'general' });
  }

  /**
   * Cancel PDF generation if it's currently in progress
   */
  public cancelPDFGeneration(): void {
    if (this.isGenerating) {
      //console.log('Cancelling PDF generation...');
      this.onShowLoader(false);
      this.isCancelled = true;
      this.isGenerating = false;
      this.emitProgress(0, 0, 'PDF generation cancelled', 0);
    }
  }

  generatePdfFromHtml() {
    // Generate PDF from HTML using html2pdf.js
    const pdfPages = document.querySelectorAll('.pdf-page');
    if (!pdfPages.length) {
      console.error('No elements with class "pdf-page" found.');
      return;
    }
    // Concatenate all .pdf-page elements into a single container
    const container = document.createElement('div');
    pdfPages.forEach(page => {
      // Clone the node to avoid moving it from the DOM
      container.appendChild(page.cloneNode(true));
    });

    // html2pdf options
    const opt = {
      margin: 0,
      filename: 'angular-generated.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
    };

    // @ts-ignore
    if (typeof html2pdf !== 'undefined') {
      // @ts-ignore
      html2pdf().from(container).set(opt).save();
    } else {
      console.error('html2pdf.js is not loaded.');
    }
  }

  generatePDFWithDocRaptor() {

  }

  generatePDFWithPuppeteer() {
    try {
      // Get all elements with class 'pdf-page' and concatenate their outerHTML
      const pdfContainer: any = document.getElementById('pdf-container');
      const pdfPages: any = pdfContainer?.querySelectorAll('.rider-comparison-table');

      // Get the p-chart with id 'allocation-pdf-chart-container'
      const allocationChartContainer = document.querySelectorAll('.allocation-pdf-chart-container');
      if (allocationChartContainer) {
        // Find the canvas element inside this p-chart container
        for (let i = 0; i < allocationChartContainer.length; i++) {
          const canvas = allocationChartContainer[i].querySelector('canvas');
          if (canvas) {
            // Create an image with the same style as the canvas
            const img = document.createElement('img');
            img.src = canvas.toDataURL('image/png');
            // Copy canvas style attributes to image
            img.style.width = canvas.style.width || canvas.width + 'px';
            img.style.height = canvas.style.height || canvas.height + 'px'; 
            img.style.margin = canvas.style.margin;
            img.style.maxWidth = canvas.style.maxWidth;
            img.style.maxHeight = canvas.style.maxHeight;
            img.style.boxSizing = canvas.style.boxSizing;
            // Replace the canvas with the image in the DOM
            canvas.replaceWith(img);
          }
        }
      }
      setTimeout(()=>{
        let sections = [];
        let htmlContent = '';
        for (let i = 0; i < pdfPages.length; i++) {
          const innerTable = pdfPages[i].querySelector('p-table');
          if (innerTable) {
            if (innerTable.classList.contains('pdf-interest-table') || innerTable.classList.contains('gmwb-table')) {
            // Do something if it has the class, e.g., log or process
              // For now, just log for demonstration
              sections.push({ html: innerTable.innerHTML, orientation: 'landscape' })
            } else {
              sections.push({ html: innerTable.innerHTML, orientation: 'portrait' })
            }
            // htmlContent += innerTable.innerHTML;
          }else if (pdfPages[i].querySelector('#allocation-table')) {
              let allocationTable = pdfPages[i].querySelector('#allocation-table');
              if (allocationTable.classList.contains('allocation-table-container')) {
                sections.push({ html: allocationTable.innerHTML, orientation: 'landscape' })
              }
          }
        }
        const pdfApiUrl = environment.pdfApiUrl;
        // Get the token from sessionStorage
        const token = sessionStorage.getItem('Token');
        const headers = token
          ? { Authorization: `Bearer ${token}` }
          : {};
        this.http.post(pdfApiUrl, { sections }, {
          responseType: 'blob' as 'json', // TypeScript expects 'json', but we want 'blob'
          observe: 'events' as 'events',
          reportProgress: true,
          headers: new HttpHeaders(headers as any)
        }).subscribe({
          next: (event: any) => {
            if (event.type === 1) { // HttpEventType.Sent
              // Request sent
              // You can show a spinner or progress bar here
              // e.g., this.progress = 0;
            } else if (event.type === 3) { // HttpEventType.DownloadProgress
              // Download in progress
              if (event.total) {
                const percentDone = Math.round(100 * event.loaded / event.total);
              }
            } else if (event.type === 4) { // HttpEventType.Response
              // Download complete
              const blob = new Blob([event.body], { type: 'application/pdf' });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `Product-Comparison-${new Date().getTime()}.pdf`;
              document.body.appendChild(a);
              a.click();
              setTimeout(() => {
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
              }, 100);
              this.showLoader.emit(false)
            }
          },
          error: (err: any) => {
            console.error('Error generating PDF:', err);
            // Hide progress bar/spinner here
            // e.g., this.progress = null;
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error while generating PDF' });
            this.showLoader.emit(false)
          }
        });
      },500)
      
    } catch (e) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error while generating PDF' });
      this.showLoader.emit(false)
    }
  }


  generateProductProfilePDF(fileName: string = `Product-Comparison-${new Date().getTime()}`) {
    try {
      const pdfContainer: any = document.getElementById('pdf-container');
      const pdfPages: any = pdfContainer?.querySelectorAll('.pdf-page');
      let sections = [];
      let htmlContent = '';
      console.log('pdfPages.length', pdfPages.length);
      for (let i = 0; i < pdfPages.length; i++) {
        const orientation = i > 0 && i % 2 === 0 && pdfPages[i].classList.contains('indexed-interest-pdf-page') ? 'landscape' : 'portrait';  
        sections.push({ html: pdfPages[i].innerHTML, orientation: orientation });
      }
      const pdfApiUrl = environment.pdfApiUrl;
      // Get the token from sessionStorage
      const token = sessionStorage.getItem('Token');
      const headers = token
        ? { Authorization: `Bearer ${token}` }
        : {};
      this.http.post(pdfApiUrl, { sections }, {
        responseType: 'blob' as 'json', // TypeScript expects 'json', but we want 'blob'
        observe: 'events' as 'events',
        reportProgress: true,
        headers: new HttpHeaders(headers as any)
      }).subscribe({
        next: (event: any) => {
          if (event.type === 1) { // HttpEventType.Sent
            // Request sent
            // You can show a spinner or progress bar here
            // e.g., this.progress = 0;
          } else if (event.type === 3) { // HttpEventType.DownloadProgress
            // Download in progress
            if (event.total) {
              const percentDone = Math.round(100 * event.loaded / event.total);
            }
          } else if (event.type === 4) { // HttpEventType.Response
            // Download complete
            const blob = new Blob([event.body], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${fileName}.pdf`;
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
              document.body.removeChild(a);
              window.URL.revokeObjectURL(url);
            }, 100);
            this.showLoader.emit(false)
            // Hide progress bar/spinner here
            // e.g., this.progress = null;
          }
        },
        error: (err: any) => {
          // console.error('Error generating PDF:', err);
          // Hide progress bar/spinner here
          // e.g., this.progress = null;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error while generating PDF' });
          this.showLoader.emit(false)
        }
      });
      
    } catch (e) {
      //console.error('Error generating PDF:', e);
    }
  }
}



